import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  FiX, FiUpload, FiFileText, FiVolume2, FiEye,
  FiBookOpen, FiAlertCircle, FiTrash2, FiEdit, FiDownload, FiPlus, FiExternalLink, FiMail, FiArrowLeft, FiCopy, FiLayers, FiActivity, FiRefreshCw, FiCheckCircle
} from 'react-icons/fi';
import apiService from '../../services/api';
import RichTextEditorTipTap from '../common/RichTextEditor';
import Notification from '../common/Notification';
import ConfirmationModal from '../modals/ConfirmationModal';
import Tooltip from '../common/Tooltip';
import MergeRecordingsModal from './MergeRecordingsModal';
import DeleteRecordingModal from './DeleteRecordingModal';
import { formatDateForDisplay, utcToChicago, createChicagoDateTime } from '../../utils/datetime-comprehensive';
import logger from '../../utils/logger';
import styles from './AddContentModal.module.css';

// ModalOverlay removed - component now renders inline

const tabPermissionMap = {
  announcements: 'course_content:announcements:manage',
  syllabus: 'course_content:syllabus:manage',
  slides: 'course_content:slides:manage',
  readings: 'course_content:readings:manage',
  recordings: 'course_content:recordings:manage',
  notifications: 'course_content:notifications:manage',
};

// Hard cap for recording uploads — S3's maximum object size (5 TB). Enforced
// here client-side and again server-side (MAX_S3_OBJECT_BYTES in
// server/routes/course-content.js). A single uploaded file may never exceed it.
const MAX_RECORDING_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024 * 1024; // 5 TB

const sortFilesByLastModified = (files) => [...files].sort((a, b) => (a.lastModified || 0) - (b.lastModified || 0));

const formatFileTimestamp = (timestamp) => {
  if (!timestamp) return 'No timestamp';
  try {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return 'No timestamp';
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

// Recording size shown in the content list: MB until it reaches 1000 MB, then GB.
const formatRecordingFileSize = (bytes) => {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1000) {
    return `${(mb / 1024).toFixed(2)} GB`;
  }
  return `${mb.toFixed(2)} MB`;
};

// The final S3 file name an uploaded recording will get, derived from its date
// (mirrors the server's recordingDateFileName: "MM_DD_YYYY.mp4"). Used to detect
// a collision with an existing recording before uploading.
const recordingFinalFileName = (recordingDate) => {
  const match = typeof recordingDate === 'string' ? recordingDate.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
  return match ? `${match[2]}_${match[3]}_${match[1]}.mp4` : null;
};

// Reusable File Upload Field Component
const FileUploadField = ({ 
  id, 
  label, 
  optional = false, 
  accept, 
  helperText, 
  value, 
  onChange, 
  onClear, 
  error,
  maxSize,
  existingFileName,
  existingFileUrl,
  onSizeExceeded,
  multiple = false
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleSelectedFiles = (files, inputElement = null) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (selectedFiles.length > 0) {
      // Client-side validation
      const oversized = maxSize ? selectedFiles.find(file => file.size > maxSize) : null;
      if (oversized) {
        const maxMb = (maxSize / 1024 / 1024).toFixed(0);
        if (onSizeExceeded) {
          onSizeExceeded(maxMb);
        }
        if (inputElement) {
          inputElement.value = '';
        }
        return;
      }
      onChange(multiple ? selectedFiles : selectedFiles[0]);
    }
  };

  const handleFileChange = (e) => {
    handleSelectedFiles(e.target.files, e.target);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    handleSelectedFiles(e.dataTransfer.files, document.getElementById(id));
  };

  const selectedFiles = Array.isArray(value) ? value : (value ? [value] : []);
  const hasSelectedFile = selectedFiles.length > 0;
  const hasExistingFile = !!existingFileName && !hasSelectedFile;

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>
        {label}
        {optional && <span className={styles.optionalLabel}> (Optional)</span>}
      </label>
      <div 
        className={`${styles.fileUploadArea} ${hasSelectedFile ? styles.hasFile : ''} ${isDragging ? styles.dragging : ''}`}
        onClick={() => document.getElementById(id).click()}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <FiUpload size={24} />
        <div className={styles.uploadText}>
          {hasSelectedFile ? `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} selected. Upload or click to add more files.` : hasExistingFile ? 'Click to upload a replacement file' : 'Upload files or drag and drop them here'}
        </div>
        {hasExistingFile && (
          <div className={styles.helperText}>
            A file is already uploaded. Selecting a new file will replace it.
          </div>
        )}
        {helperText && (
          <div className={styles.helperText}>
            {helperText}
          </div>
        )}
        {hasSelectedFile && (
          <>
            <div className={styles.fileName}>
              {selectedFiles.length === 1 ? selectedFiles[0].name : selectedFiles.map(file => file.name).join(', ')}
            </div>
            <button
              type="button"
              className={styles.clearFileButton}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
                document.getElementById(id).value = '';
              }}
            >
              {selectedFiles.length === 1 ? 'Remove File' : 'Remove Files'}
            </button>
          </>
        )}
        {!value && existingFileName && (
          <div className={styles.existingFileInfo}>
            <span className={styles.uploadedFileBadge}>
              <FiUpload size={12} />
              File uploaded
            </span>
            <span className={styles.existingFileName}>{existingFileName}</span>
            {existingFileUrl && (
              <a
                className={styles.viewExistingFileLink}
                href={existingFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <FiExternalLink size={12} />
                View current file
              </a>
            )}
          </div>
        )}
        <input
          id={id}
          className={styles.fileInput}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
        />
      </div>
      {error && (
        <div className={styles.errorText}>{error}</div>
      )}
    </div>
  );
};

// Tabs now use CSS module - see styles.tabsContainer and styles.tab

// Form components removed - using CSS module classes instead

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 1.5rem;
`;

// ContentItem, ContentInfo, ContentIcon, ContentDetails, ContentTitle, ContentMeta, ContentActions removed - using CSS module resourceCard classes instead

const ActionButton = styled.button`
  background: rgba(216, 184, 137, 0.08);
  border: 1px solid rgba(216, 184, 137, 0.15);
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.625rem;
  border-radius: 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    background-color: rgba(216, 184, 137, 0.15);
    color: var(--text-primary);
    border-color: var(--primary-color);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.2);
  }
  
  &.delete:hover {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    box-shadow: 0 4px 12px rgba(252, 129, 129, 0.2);
  }
  
  @media (max-width: 768px) {
    min-width: 44px;
    min-height: 44px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem;
  color: var(--text-secondary);
  background: linear-gradient(135deg, rgba(216, 184, 137, 0.03), rgba(216, 184, 137, 0.06));
  border-radius: 0;
  border: 1px dashed rgba(216, 184, 137, 0.25);
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

function AddContentModal({ onClose, course, elective, onContentAdded, initialTab = 'announcements' }) {
  const [currentUser, setCurrentUser] = useState(null);
  // Ensure activeTab is always a string
  const [activeTab, setActiveTab] = useState(() => {
    const tab = initialTab || 'announcements';
    return typeof tab === 'string' ? tab : 'announcements';
  });
  const [readingsSubTab, setReadingsSubTab] = useState('new'); // 'new' or 'existing'
  const [recordingsSubTab, setRecordingsSubTab] = useState('new'); // 'new' or 'existing'
  const [announcementsSubTab, setAnnouncementsSubTab] = useState('new'); // 'new' or 'existing'
  const [syllabusSubTab, setSyllabusSubTab] = useState('new'); // 'new' or 'existing'
  const [slidesSubTab, setSlidesSubTab] = useState('new'); // 'new' or 'existing'
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  // Sliding window of recent progress samples + derived ETA (seconds) for the
  // progress card. A windowed average is far steadier than per-event byte deltas.
  const uploadSamplesRef = useRef([]); // [{ t, loaded }] within the time window
  const lastEtaUpdateRef = useRef(0);  // throttle how often the ETA re-renders
  const [uploadEtaSeconds, setUploadEtaSeconds] = useState(null);
  const submittingRef = useRef(false);
  // Tracks the in-flight recording upload so it can be cancelled and so any
  // bytes already written to the ingest bucket can be cleaned up on
  // cancellation or a dropped connection. Shape:
  //   { kind: 'single'|'merge', controller, courseId, key, uploadId, keys: [],
  //     placeholderId, cancelledByUser }
  const uploadAbortRef = useRef(null);
  const [cancellingUpload, setCancellingUpload] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', confirmText: 'Confirm', type: 'danger', onConfirm: null, isLoading: false });
  const [effectivePermissions, setEffectivePermissions] = useState(null);
  const notify = (type, title, message) => setNotification({ isOpen: true, type, title, message });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  const sanitizeErrorForLog = (error) => ({
    name: error?.name,
    message: sanitizeAwsSensitiveText(error?.message),
    status: error?.status,
  });
  const [editingContent, setEditingContent] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeStatusModal, setMergeStatusModal] = useState({
    isOpen: false,
    loading: false,
    contentId: null,
    contentTitle: '',
    data: null,
    error: null,
  });
  const [uploadCompleteModal, setUploadCompleteModal] = useState({ isOpen: false, contentId: null, contentTitle: '' });
  const [deleteRecordingModal, setDeleteRecordingModal] = useState({
    isOpen: false,
    loading: false,
    deleting: false,
    contentId: null,
    recording: null,
    serving: null,
    ingest: null,
    confirmText: '',
    error: null,
  });
  // Overwrite prompt shown when a recording already exists with the same final
  // file name. Reuses the same file-list + typed-confirmation UI as delete.
  const [overwriteModal, setOverwriteModal] = useState({
    isOpen: false,
    loading: false,
    deleting: false,
    contentId: null,
    recording: null,
    serving: null,
    ingest: null,
    confirmText: '',
    error: null,
    collidingCount: 1,
    // 'upload' = delete then re-run the upload (collision found at submit).
    // 'clear'  = delete only (user picked an existing session/date up front).
    mode: 'upload',
  });
  // Set true once the user confirms overwrite, so the re-run upload skips the
  // collision check. Reset at the end of each submit.
  const overwriteConfirmedRef = useRef(false);
  // One-shot: keep the Recordings sub-tab on "New" through the next auto-switch
  // pass (e.g. after cancelling a proactive overwrite prompt blanks the date).
  const keepRecordingsNewTabRef = useRef(false);

  const fetchMergeStatus = useCallback(async (contentId, contentTitle) => {
    setMergeStatusModal(prev => ({
      ...prev,
      isOpen: true,
      loading: true,
      contentId,
      contentTitle: contentTitle ?? prev.contentTitle ?? '',
      error: null,
    }));
    try {
      const res = await apiService.getRecordingMergeStatus(contentId);
      if (res?.success) {
        setMergeStatusModal(prev => ({ ...prev, loading: false, data: res, error: null, lastFetchedAt: Date.now() }));
      } else {
        setMergeStatusModal(prev => ({ ...prev, loading: false, error: sanitizeAwsSensitiveText(res?.message || 'Failed to fetch job status'), lastFetchedAt: Date.now() }));
      }
    } catch (err) {
      setMergeStatusModal(prev => ({ ...prev, loading: false, error: sanitizeAwsSensitiveText(err?.message || 'Failed to fetch job status'), lastFetchedAt: Date.now() }));
    }
  }, []);
  const [content, setContent] = useState({
    recordings: [],
    announcements: [],
    syllabus: [],
    slides: [],
    readings: []
  });
  
  // Form states for each content type
  const [formData, setFormData] = useState({
    title: '',
    description: { content_json: null, content_html: '<p></p>', content_plain: '' },
    file: null,
    files: [],
    url: '',
    urls: [], // For multiple URLs in readings
    priority: 'medium',
    week: '',
    recordingDate: '',
    sendEmail: true // Email notification toggle
  });
  // Pre-existing attendance sessions for the course. When the recording form
  // is open we offer a dropdown of "Week N — MM/DD/YYYY" options so the
  // uploader can match a recording to a real attendance session in one click
  // instead of typing the week and date manually.
  const [attendanceSessions, setAttendanceSessions] = useState([]);
  const [selectedAttendanceSessionId, setSelectedAttendanceSessionId] = useState('');
  const [videoHostingType, setVideoHostingType] = useState('upload'); // 'external' or 'upload'
  const [slidesSourceType, setSlidesSourceType] = useState('upload'); // 'upload' or 'external'
  const [syllabusSourceType, setSyllabusSourceType] = useState('upload'); // 'upload' or 'external'

  const getDefaultSubTab = useCallback((tab, contentData) => {
    if (tab === 'notifications') return 'new';
    return (contentData?.[tab] || []).length > 0 ? 'existing' : 'new';
  }, []);

  const setSubTabForTab = useCallback((tab, value) => {
    switch (tab) {
      case 'recordings':
        setRecordingsSubTab(value);
        break;
      case 'announcements':
        setAnnouncementsSubTab(value);
        break;
      case 'syllabus':
        setSyllabusSubTab(value);
        break;
      case 'slides':
        setSlidesSubTab(value);
        break;
      case 'readings':
        setReadingsSubTab(value);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('faithinsight_user') || localStorage.getItem('user');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (error) {
      logger.warn('Unable to parse user from localStorage', { error });
      setCurrentUser(null);
    }
  }, []);

  // Helper function to get email notification setting from localStorage
  const getEmailNotificationSetting = useCallback(() => {
    try {
      const savedSetting = localStorage.getItem('faithinsight_email_notifications');
      if (savedSetting) {
        const { enabled } = JSON.parse(savedSetting);
        return enabled;
      }
    } catch (error) {
      logger.error('Failed to load email notification setting', { error });
    }
    return formData.sendEmail; // Fallback to form data
  }, [formData.sendEmail]);

  const fetchContent = useCallback(async () => {
    try {
      // Management view: include still-processing recordings so admins/managers
      // can monitor conversion and delete stuck uploads. The playlist
      // (CourseDetail) omits this flag, so processing recordings stay hidden there.
      const response = elective
        ? await apiService.getElectiveContent(elective.id, { includeProcessing: true })
        : await apiService.getCourseContent(course.id, { includeProcessing: true });
      if (response.success) {
        setContent(response.content);
      }
    } catch (error) {
      logger.error('Error fetching content', { error, courseId: course?.id, electiveId: elective?.id });
      // Fallback to empty content
      setContent({
        recordings: [],
        announcements: [],
        syllabus: [],
        slides: [],
        readings: []
      });
    }
  }, [course, elective]);

  const fetchEffectivePermissions = useCallback(async () => {
    try {
      const scopeType = elective ? 'elective' : 'course';
      const scopeId = elective?.id || course?.id;
      if (!scopeId) return;
      const response = await apiService.getEffectivePermissions({ scopeType, scopeId });
      if (response.success) {
        setEffectivePermissions(response.permissions || []);
      }
    } catch (error) {
      logger.error('Error fetching effective permissions', { error, courseId: course?.id, electiveId: elective?.id });
      setEffectivePermissions([]);
    }
  }, [course, elective]);

  // Update activeTab when initialTab changes
  useEffect(() => {
    if (initialTab && typeof initialTab === 'string') {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    // One-shot override: stay on New for this pass (e.g. cancelling a proactive
    // overwrite prompt just blanked the date but we want to keep the form open).
    if (keepRecordingsNewTabRef.current) {
      keepRecordingsNewTabRef.current = false;
      return;
    }
    // Don't auto-switch the sub-tab while the user is actively creating a new
    // recording — an upload in progress, or a New-tab recording form that
    // already has a file or date entered. A background content refresh (e.g.
    // after clearing a recording for overwrite) would otherwise flip to the
    // Existing tab and pull them off the New form / hide the progress bar.
    const buildingNewRecording = activeTab === 'recordings' && (
      !!formData.file || (formData.files && formData.files.length > 0) ||
      (typeof formData.recordingDate === 'string' && formData.recordingDate.trim() !== '')
    );
    if (!editingContent && !loading && !uploadProgress && !buildingNewRecording) {
      setSubTabForTab(activeTab, getDefaultSubTab(activeTab, content));
    }
  }, [activeTab, content, editingContent, loading, uploadProgress, formData.file, formData.files, formData.recordingDate, getDefaultSubTab, setSubTabForTab]);

  // Load email notification setting when notifications tab is selected
  useEffect(() => {
    if (activeTab === 'notifications') {
      try {
        const savedSetting = localStorage.getItem('faithinsight_email_notifications');
        logger.debug('Loading email notification setting for notifications tab', { savedSetting });
        if (savedSetting) {
          const { enabled } = JSON.parse(savedSetting);
          logger.debug('Setting email notification', { enabled });
          setFormData(prev => ({ ...prev, sendEmail: enabled }));
        } else {
          logger.debug('No saved email notification setting, using default', { default: true });
        }
      } catch (error) {
        logger.error('Failed to load email notification setting', { error });
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (course || elective) {
      fetchContent();
      fetchEffectivePermissions();

      // Load email notification setting
      try {
        const savedSetting = localStorage.getItem('faithinsight_email_notifications');
        logger.debug('Loading email notification setting', { savedSetting });
        if (savedSetting) {
          const { enabled } = JSON.parse(savedSetting);
          logger.debug('Setting email notification', { enabled });
          setFormData(prev => ({ ...prev, sendEmail: enabled }));
        } else {
          logger.debug('No saved email notification setting, using default', { default: true });
        }
      } catch (error) {
        logger.error('Failed to load email notification setting', { error });
        // Keep default value (true)
      }
    }
  }, [course, elective, fetchContent, fetchEffectivePermissions]);

  // Pull existing attendance sessions for the course so the recording form can
  // offer them as quick-select options. Only courses are supported today —
  // electives use the same form but the attendance endpoint is course-scoped.
  useEffect(() => {
    if (!course?.id) {
      setAttendanceSessions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getAttendanceSessions(course.id);
        if (cancelled) return;
        const list = Array.isArray(res?.sessions) ? res.sessions : (Array.isArray(res) ? res : []);
        setAttendanceSessions(list);
      } catch (error) {
        if (cancelled) return;
        logger.error('Failed to load attendance sessions for recording form', { error, courseId: course.id });
        setAttendanceSessions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [course?.id]);

  // When opening the New Recording form (not editing), default the title to
  // "Full Session" so the uploader can submit straight away. Doesn't fire while
  // editing existing content — those rows keep whatever was already saved.
  useEffect(() => {
    if (activeTab === 'recordings' && !editingContent && !formData.title) {
      setFormData(prev => ({ ...prev, title: 'Full Session' }));
    }
  }, [activeTab, editingContent, formData.title]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'description') {
      // Handle description as an object with content properties
      setFormData(prev => ({ 
        ...prev, 
        [name]: { 
          content_json: null, 
          content_html: `<p>${value}</p>`, 
          content_plain: value 
        } 
      }));
    } else if (name === 'url' && activeTab === 'recordings' && value.trim()) {
      // Clear file when URL is entered for recordings
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        file: null,
        files: []
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle multiple URLs for readings
  const addUrl = () => {
    setFormData(prev => ({
      ...prev,
      urls: [...(prev.urls || []), { id: Date.now(), url: '', label: '' }]
    }));
  };

  const removeUrl = (id) => {
    setFormData(prev => ({
      ...prev,
      urls: (prev.urls || []).filter(url => url.id !== id)
    }));
  };

  const updateUrl = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      urls: (prev.urls || []).map(url => 
        url.id === id ? { ...url, [field]: value } : url
      )
    }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSubTabForTab(tab, getDefaultSubTab(tab, content));
    resetForm(); // Reset form and editing state when switching tabs
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: { content_json: null, content_html: '<p></p>', content_plain: '' },
      file: null,
      files: [],
      url: '',
      urls: [],
      priority: 'medium',
      week: '',
      recordingDate: '',
      sendEmail: true
    });
    setEditingContent(null);
    setVideoHostingType('upload');
    setSlidesSourceType('upload');
    setSyllabusSourceType('upload');
  };

  const setRecordingFiles = (files) => {
    setFormData(prev => ({ ...prev, file: files[0] || null, files }));
  };

  const moveRecordingFile = (index, direction) => {
    setFormData(prev => {
      const files = [...(prev.files || [])];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= files.length) return prev;
      [files[index], files[nextIndex]] = [files[nextIndex], files[index]];
      return { ...prev, file: files[0] || null, files };
    });
  };

  const removeRecordingFile = (index) => {
    setFormData(prev => {
      const files = (prev.files || []).filter((_, i) => i !== index);
      return { ...prev, file: files[0] || null, files };
    });
  };

  const sortRecordingFilesByTimestamp = () => {
    setFormData(prev => {
      const files = sortFilesByLastModified(prev.files || []);
      return { ...prev, file: files[0] || null, files };
    });
  };

  const navItems = React.useMemo(() => [
    { id: 'announcements', label: 'Announcements', icon: FiAlertCircle },
    { id: 'syllabus', label: 'Syllabus', icon: FiBookOpen },
    { id: 'slides', label: 'Slides', icon: FiFileText },
    { id: 'readings', label: 'Readings', icon: FiBookOpen },
    { id: 'recordings', label: 'Recordings', icon: FiVolume2 },
    { id: 'notifications', label: 'Notifications', icon: FiMail },
  ].filter(item => !effectivePermissions || effectivePermissions.includes(tabPermissionMap[item.id])), [effectivePermissions]);

  useEffect(() => {
    if (effectivePermissions && navItems.length > 0 && !navItems.some(item => item.id === activeTab)) {
      const nextTab = navItems[0].id;
      setActiveTab(nextTab);
      setSubTabForTab(nextTab, getDefaultSubTab(nextTab, content));
      resetForm();
    }
  }, [effectivePermissions, navItems, activeTab, content, getDefaultSubTab, setSubTabForTab]);

  const isLocalFileUrl = (url) => typeof url === 'string' && /^\s*file:\/\//i.test(url);

  const hasLocalFileUrl = () => {
    if (isLocalFileUrl(formData.url)) {
      return true;
    }

    return Array.isArray(formData.urls) && formData.urls.some(urlItem => isLocalFileUrl(urlItem.url));
  };

  const getExistingFileName = (contentItem = editingContent) => (
    contentItem?.file_name || (contentItem?.file_path ? contentItem.file_path.split('/').pop() : '')
  );

  const getExistingFileUrl = (contentItem = editingContent) => {
    if (contentItem?.type === 'recording' && contentItem?.url) {
      return contentItem.url;
    }

    const filePath = contentItem?.file_path || contentItem?.file_name;
    return filePath ? `${apiService.baseURL}/course-files/${encodeURIComponent(filePath.split('/').pop())}` : '';
  };

  const getDownloadUrl = (url, fileName = 'recording') => {
    if (!url) return '';

    try {
      const downloadUrl = new URL(url, window.location.origin);
      downloadUrl.searchParams.set('response-content-disposition', `attachment; filename="${fileName}"`);
      return downloadUrl.toString();
    } catch (error) {
      return url;
    }
  };

  const formatUploadBytes = (bytes) => {
    if (!bytes || bytes < 1024 * 1024 * 1024) {
      return `${((bytes || 0) / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // The remaining-time estimate as a bare value (no "remaining" suffix). Seconds
  // near the end, minutes in the middle, hours for very large uploads — so the
  // number stays meaningful. Shown on its own line as
  // "Estimated time remaining: <value>".
  const formatUploadEta = (seconds) => {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
    if (seconds < 1) return 'almost done';
    if (seconds < 60) {
      const s = Math.max(1, Math.round(seconds));
      return `${s} second${s === 1 ? '' : 's'}`;
    }
    if (seconds < 3600) {
      const mins = Math.max(1, Math.round(seconds / 60));
      return `${mins} minute${mins === 1 ? '' : 's'}`;
    }
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return `${hrs}h${mins ? ` ${mins}m` : ''}`;
  };

  // Estimate time-remaining from the average upload speed over a sliding time
  // window (last ~5s). This is much steadier than the instantaneous delta
  // between two progress events — which is noisy, especially for the parallel
  // multipart path where four parts report progress independently and in bursts.
  useEffect(() => {
    if (!uploadProgress || !uploadProgress.total || uploadProgress.loaded == null) {
      uploadSamplesRef.current = [];
      lastEtaUpdateRef.current = 0;
      setUploadEtaSeconds(null);
      return;
    }

    const ETA_WINDOW_MS = 5000;   // average throughput over the last 5 seconds
    const ETA_THROTTLE_MS = 750;  // cap how often the displayed estimate changes
    const now = Date.now();
    const { loaded, total } = uploadProgress;
    const samples = uploadSamplesRef.current;

    // A new file/upload reset the byte counter — start a fresh window so the
    // average doesn't get poisoned by the previous file's samples.
    if (samples.length && loaded < samples[samples.length - 1].loaded) {
      samples.length = 0;
    }
    samples.push({ t: now, loaded });

    // Drop samples older than the window, keeping at least two to measure a slope.
    while (samples.length > 2 && now - samples[0].t > ETA_WINDOW_MS) {
      samples.shift();
    }

    if (loaded >= total) {
      lastEtaUpdateRef.current = now;
      setUploadEtaSeconds(0);
      return;
    }

    const first = samples[0];
    const spanSec = (now - first.t) / 1000;
    const spanBytes = loaded - first.loaded;

    // Wait for a meaningful span before showing a number; while stalled (no
    // bytes moved in the window) keep the previous estimate rather than spiking.
    if (spanSec < 0.75 || spanBytes <= 0) return;
    if (now - lastEtaUpdateRef.current < ETA_THROTTLE_MS) return;

    lastEtaUpdateRef.current = now;
    const speed = spanBytes / spanSec; // bytes/sec averaged over the window
    setUploadEtaSeconds(Math.max(0, total - loaded) / speed);
  }, [uploadProgress]);


  const openNativeDatePicker = (event) => {
    // showPicker() requires a user gesture (real click) — guard against
    // programmatic focus/triggers throwing NotAllowedError into the console.
    try {
      event.currentTarget.showPicker?.();
    } catch (e) {
      // No-op: the input still works via the default browser picker UI.
    }
  };

  // Parse "Week N" / "Week #N" / "WEEK 3 - lecture" out of a session title.
  // Mirrors the dashboard helper so the recording form picks the same number.
  const parseWeekFromTitle = (title) => {
    if (!title) return null;
    const m = String(title).match(/week\s*#?\s*(\d+)/i);
    return m ? Number(m[1]) : null;
  };

  // Normalize whatever date format the backend hands us into the YYYY-MM-DD
  // value that <input type="date"> expects.
  const formatSessionDateForInput = (rawDate) => {
    if (!rawDate) return '';
    if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
      return rawDate.slice(0, 10);
    }
    try {
      const d = new Date(rawDate);
      if (Number.isNaN(d.getTime())) return '';
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  // Build "Week N — MM/DD/YYYY" labels for the session picker. Sessions
  // without a parseable week number are omitted since the picker's whole
  // point is to wire a known week+date together in one click.
  const attendanceSessionOptions = React.useMemo(() => {
    return (attendanceSessions || [])
      .map(s => {
        const week = parseWeekFromTitle(s.title);
        const date = formatSessionDateForInput(s.date);
        if (!week || !date) return null;
        const [y, m, d] = date.split('-');
        return {
          id: String(s.id),
          week,
          date,
          label: `Week ${week} — ${m}/${d}/${y}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.week - b.week);
  }, [attendanceSessions]);

  const handleAttendanceSessionSelect = (e) => {
    const id = e.target.value;
    setSelectedAttendanceSessionId(id);
    if (!id || id === 'custom') return;
    const opt = attendanceSessionOptions.find(o => o.id === id);
    if (!opt) return;
    setFormData(prev => ({
      ...prev,
      week: String(opt.week),
      recordingDate: opt.date,
    }));
    // Prompt to overwrite if this session's date already has a recording.
    maybePromptOverwriteForDate(opt.date);
  };

  const isViewableFile = (fileNameOrPath) => {
    if (!fileNameOrPath || typeof fileNameOrPath !== 'string') {
      return false;
    }

    const fileName = fileNameOrPath.split('/').pop().split('?')[0].split('#')[0];
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'pdf' || extension === 'html' || extension === 'htm';
  };

  const copyTextToClipboard = async (text, successMessage = 'Link copied to clipboard.') => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Copied',
        message: successMessage
      });
    } catch (error) {
      logger.error('Failed to copy text to clipboard', { error });
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Copy failed',
        message: 'Failed to copy link. Please copy manually.'
      });
    }
  };

  // Helper function to safely get singular form of tab name
  const getSingularTabName = (tab) => {
    const safeTab = typeof tab === 'string' ? tab : 'recordings';
    
    // Map frontend tab names to database content types
    const tabMapping = {
      'recordings': 'recording',
      'announcements': 'announcement', 
      'syllabus': 'syllabus',
      'slides': 'slide',
      'readings': 'reading'
    };
    
    return tabMapping[safeTab] || safeTab.slice(0, -1);
  };

  const handleNotificationsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      logger.debug('Current formData.sendEmail', { sendEmail: formData.sendEmail });
      
      // Save email notification setting to localStorage
      // In a real implementation, this would be saved to user preferences in the database
      const settingData = {
        enabled: formData.sendEmail,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem('faithinsight_email_notifications', JSON.stringify(settingData));
      logger.debug('Email notification setting saved', { settingData });
      
      notify(
        'success',
        'Settings saved',
        `Email notifications are now ${formData.sendEmail ? 'enabled' : 'disabled'}.`
      );

      setLoading(false);
      onClose(); // Close the modal after saving
    } catch (error) {
      logger.error('Failed to save notification settings', { error });
      notify('error', 'Save failed', 'Failed to save notification settings. Please try again.');
      setLoading(false);
    }
  };

  // Delete anything the cancelled/interrupted upload already wrote to the ingest
  // bucket. The cleanup call itself needs the network, so on a transient drop we
  // retry with backoff to recover promptly. If every attempt fails (e.g. the
  // connection stays down or the tab is about to close), the ingest bucket's
  // 1-day lifecycle rule is the guaranteed backstop that removes the leftovers.
  const cleanupCancelledUpload = useCallback(async () => {
    const info = uploadAbortRef.current;
    if (!info?.courseId) return;
    const keys = info.keys && info.keys.length ? info.keys : (info.key ? [info.key] : []);
    if (keys.length === 0 && !info.uploadId) return;

    // Immediate try, then two backoff retries (~7s worst case). A normal cancel
    // with a healthy connection succeeds on the first attempt with no delay.
    const delaysMs = [0, 2000, 5000];
    for (let attempt = 0; attempt < delaysMs.length; attempt++) {
      if (delaysMs[attempt] > 0) {
        await new Promise(resolve => setTimeout(resolve, delaysMs[attempt]));
      }
      try {
        await apiService.cleanupIngestRecording(info.courseId, {
          key: info.key,
          keys,
          uploadId: info.uploadId,
        });
        return; // cleaned up
      } catch (error) {
        const lastAttempt = attempt === delaysMs.length - 1;
        logger.warn('Failed to clean up cancelled recording upload', {
          attempt: attempt + 1,
          willRetry: !lastAttempt,
          error: sanitizeErrorForLog(error),
        });
        if (lastAttempt) {
          logger.warn('Cleanup exhausted retries; ingest 1-day lifecycle rule will remove leftover files', {
            courseId: info.courseId,
          });
        }
      }
    }
  }, []);

  // Cancel button handler: abort the in-flight upload. The abort surfaces as an
  // AbortError in handleSubmit's catch, which runs cleanupCancelledUpload.
  const handleCancelUpload = useCallback(() => {
    const info = uploadAbortRef.current;
    if (!info?.controller) return;
    info.cancelledByUser = true;
    setCancellingUpload(true);
    info.controller.abort();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current || loading) {
      logger.warn('Duplicate content submission blocked on client', { activeTab, courseId: course?.id, electiveId: elective?.id });
      return;
    }

    submittingRef.current = true;

    logger.debug('Form submission started', { formData, urls: formData.urls, activeTab });

    // Validate title is not empty
    if (!formData.title || !formData.title.trim()) {
      notify('warning', 'Title required', 'Please enter a title.');
      submittingRef.current = false;
      return;
    }

    if (hasLocalFileUrl()) {
      notify('warning', 'Invalid link', 'Local file URLs are not allowed. Please upload the file or use a public https:// link.');
      submittingRef.current = false;
      return;
    }

    // If we're editing existing content, use the update handler
    if (editingContent) {
      submittingRef.current = false;
      return handleUpdateContent(e);
    }
    
    setLoading(true);
    setUploadProgress(null);

    try {
      // Validate recordings: must select a hosting type and provide required data
      if (activeTab === 'recordings') {
        if (!videoHostingType) {
          notify('warning', 'Select hosting type', 'Please select a video hosting type (External or Upload).');
          setLoading(false);
          submittingRef.current = false;
          return;
        }

        if (videoHostingType === 'external') {
          const hasUrl = formData.url && formData.url.trim();
          if (!hasUrl) {
            notify('warning', 'Video URL required', 'Please provide a Video URL.');
            setLoading(false);
            submittingRef.current = false;
            return;
          }
        } else if (videoHostingType === 'upload') {
          const recordingFiles = formData.files?.length ? formData.files : (formData.file ? [formData.file] : []);
          const hasFile = recordingFiles.length > 0;
          if (!hasFile) {
            notify('warning', 'File required', 'Please upload one or more MP4 files.');
            setLoading(false);
            submittingRef.current = false;
            return;
          }

          // Hard 5 TB cap (S3's max object size). Belt-and-suspenders in case a
          // file got in via drag-drop or a stale selection that skipped the
          // upload field's own check.
          const oversizedFile = recordingFiles.find(file => file.size > MAX_RECORDING_UPLOAD_BYTES);
          if (oversizedFile) {
            notify('warning', 'File too large', 'Recording files cannot exceed 5 TB.');
            setLoading(false);
            submittingRef.current = false;
            return;
          }

          if (!formData.recordingDate || !formData.recordingDate.trim()) {
            notify('warning', 'Recording date required', 'Please select a recording date before uploading.');
            setLoading(false);
            submittingRef.current = false;
            return;
          }
        }
      }

      if (activeTab === 'slides') {
        const hasUploadedFile = formData.file || editingContent?.file_path || editingContent?.file_name;
        const hasUrl = formData.url && formData.url.trim();

        if (slidesSourceType === 'upload' && !hasUploadedFile) {
          notify('warning', 'File required', 'Please upload a document file.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }

        if (slidesSourceType === 'external' && !hasUrl) {
          notify('warning', 'Link required', 'Please provide a link URL.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }
      }

      if (activeTab === 'syllabus') {
        const hasUploadedFile = formData.file;
        const hasUrl = formData.url && formData.url.trim();

        if (syllabusSourceType === 'upload' && hasUrl) {
          setFormData(prev => ({ ...prev, url: '' }));
        }

        if (syllabusSourceType === 'external' && !hasUrl) {
          notify('warning', 'Link required', 'Please provide a syllabus link URL.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }

        if (syllabusSourceType === 'upload' && !hasUploadedFile) {
          notify('warning', 'File required', 'Please upload a syllabus file.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }
      }

      if (activeTab === 'slides') {
        const hasUploadedFile = formData.file || editingContent?.file_path || editingContent?.file_name;
        const hasUrl = formData.url && formData.url.trim();

        if (slidesSourceType === 'upload' && !hasUploadedFile) {
          notify('warning', 'File required', 'Please upload a document file.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }

        if (slidesSourceType === 'external' && !hasUrl) {
          notify('warning', 'Link required', 'Please provide a link URL.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }
      }

      // Use S3 direct upload for course recordings; other uploads keep the server upload path.
      let contentData;
      const idempotencyKey = `content-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const recordingFiles = formData.files?.length ? formData.files : (formData.file ? [formData.file] : []);
      const shouldMergeUploadedRecordings = !elective && activeTab === 'recordings' && videoHostingType === 'upload' && recordingFiles.length > 1;
      const shouldUploadRecordingToS3 = !elective && activeTab === 'recordings' && videoHostingType === 'upload' && recordingFiles.length === 1;

      // Overwrite guard: if a recording already exists with the same final file
      // name (date-derived S3 key), prompt before clobbering it. The overwrite
      // flow deletes that recording + its files, then re-runs this submit with
      // overwriteConfirmedRef set so we skip the check and upload straight away.
      if ((shouldMergeUploadedRecordings || shouldUploadRecordingToS3) && !overwriteConfirmedRef.current) {
        const colliding = findCollidingRecording();
        if (colliding) {
          openOverwriteModal(colliding);
          setLoading(false);
          setUploadProgress(null);
          submittingRef.current = false;
          return;
        }
      }

      if (shouldMergeUploadedRecordings) {
        let placeholderId = null;
        const abortController = new AbortController();
        uploadAbortRef.current = { kind: 'merge', controller: abortController, courseId: course.id, keys: [], placeholderId: null, cancelledByUser: false };
        try {
          const totalSize = recordingFiles.reduce((sum, file) => sum + file.size, 0);
          setUploadProgress({ percent: 0, loaded: 0, total: totalSize, fileName: recordingFiles[0]?.name || '', currentIndex: 1, totalCount: recordingFiles.length });

          const startRes = await apiService.startMergeUpload(course.id, {
            title: formData.title.trim(),
            recordingDate: formData.recordingDate,
            fileCount: recordingFiles.length,
            ...(formData.week ? { week: parseInt(formData.week, 10) } : {}),
            sendEmail: getEmailNotificationSetting(),
            allowOverwrite: overwriteConfirmedRef.current,
          });
          if (!startRes?.success) throw new Error(startRes?.message || 'Failed to start merge upload');
          placeholderId = startRes.placeholderId;
          uploadAbortRef.current.placeholderId = placeholderId;

          let completedBytes = 0;
          for (let i = 0; i < recordingFiles.length; i++) {
            const file = recordingFiles[i];
            const key = startRes.sourceKeys[i];
            const completedBeforeFile = completedBytes;
            await apiService.uploadCourseRecordingToKey(course.id, file, key, (prog) => {
              setUploadProgress({
                percent: totalSize > 0 ? Math.round(((completedBeforeFile + prog.loaded) / totalSize) * 100) : 0,
                loaded: completedBeforeFile + prog.loaded,
                total: totalSize,
                fileName: file.name,
                currentIndex: i + 1,
                totalCount: recordingFiles.length,
              });
            }, { signal: abortController.signal });
            // Track each fully-uploaded source key so it can be removed from the
            // ingest bucket if a later file's upload is cancelled/interrupted.
            if (uploadAbortRef.current) uploadAbortRef.current.keys.push(key);
            completedBytes += file.size;
            await apiService.registerMergeUploadSource(course.id, {
              placeholderId,
              position: i + 1,
              key,
              fileSize: file.size,
              originalFileName: file.name,
            });
          }

          const baseTitle = formData.title?.trim() || '';
          const weekPrefix = formData.week ? `Week ${formData.week} - ` : '';
          setUploadCompleteModal({ isOpen: true, contentId: placeholderId, contentTitle: `${weekPrefix}${baseTitle}` });
          setFormData(prev => ({
            ...prev,
            title: '',
            description: { content_json: null, content_html: '<p></p>', content_plain: '' },
            file: null,
            files: [],
            url: '',
            week: '',
            recordingDate: '',
          }));
          const fileInput = document.getElementById('recordings-file-input');
          if (fileInput) fileInput.value = '';
          // Merge upload submitted successfully — no cleanup needed.
          uploadAbortRef.current = null;
          setLoading(false);
          setUploadProgress(null);
          submittingRef.current = false;
          if (typeof fetchContent === 'function') fetchContent();
          return;
        } catch (error) {
          // Drop the source rows. On a user cancel or dropped connection, remove
          // the placeholder row entirely so it never lingers in the Existing list;
          // other failures keep a 'failed' marker. Any source objects already in
          // the ingest bucket are removed by the outer catch (cleanupCancelledUpload).
          if (placeholderId) {
            const cancelledByUser = uploadAbortRef.current?.cancelledByUser || error?.name === 'AbortError';
            const droppedConnection = error?.isNetworkError || error?.status === 0 ||
              /network error|failed to fetch|load failed/i.test(error?.message || '');
            try {
              await apiService.abortMergeUpload(course.id, placeholderId, {
                deletePlaceholder: cancelledByUser || droppedConnection,
              });
            } catch (e) { /* ignore */ }
          }
          throw error;
        }
      }

      if (shouldUploadRecordingToS3) {
        const recordingFile = recordingFiles[0];
        setUploadProgress({ percent: 0, loaded: 0, total: recordingFile.size, fileName: recordingFile.name, currentIndex: 1, totalCount: 1 });

        // Set up cancellation: the Cancel button (and the dropped-connection
        // path) abort this controller, after which we clean up the ingest bucket.
        const abortController = new AbortController();
        uploadAbortRef.current = { kind: 'single', controller: abortController, courseId: course.id, key: null, uploadId: null, cancelledByUser: false };

        // S3 single-PUT caps at 5 GB. Use multipart for larger files; below
        // the threshold keep the legacy single-PUT path (zero behavior change
        // for smaller files).
        const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MB
        let uploadedKey;
        let uploadedObjectUrl;

        if (recordingFile.size > MULTIPART_THRESHOLD) {
          const result = await apiService.uploadCourseRecordingMultipart(
            course.id,
            recordingFile,
            formData.recordingDate,
            (prog) => setUploadProgress({ ...prog, fileName: recordingFile.name, currentIndex: 1, totalCount: 1 }),
            {
              signal: abortController.signal,
              allowOverwrite: overwriteConfirmedRef.current,
              onInit: ({ key, uploadId }) => {
                if (uploadAbortRef.current) {
                  uploadAbortRef.current.key = key;
                  uploadAbortRef.current.uploadId = uploadId;
                }
              },
            }
          );
          uploadedKey = result.key;
          uploadedObjectUrl = result.objectUrl;
        } else {
          const presignResponse = await apiService.createCourseRecordingS3Upload(course.id, recordingFile, formData.recordingDate, undefined, overwriteConfirmedRef.current);
          if (!presignResponse.success) {
            setUploadProgress(null);
            throw new Error(presignResponse.message || 'Failed to prepare S3 upload');
          }
          if (uploadAbortRef.current) uploadAbortRef.current.key = presignResponse.key;
          await apiService.uploadToS3(
            presignResponse.uploadUrl,
            recordingFile,
            presignResponse.headers,
            (prog) => setUploadProgress({ ...prog, fileName: recordingFile.name, currentIndex: 1, totalCount: 1 }),
            abortController.signal
          );
          uploadedKey = presignResponse.key;
          uploadedObjectUrl = presignResponse.objectUrl;
        }

        // Upload finished successfully — no cleanup needed.
        uploadAbortRef.current = null;

        contentData = {
          type: getSingularTabName(activeTab),
          title: formData.title,
          priority: formData.priority,
          url: uploadedObjectUrl,
          fileName: recordingFile.name,
          fileSize: recordingFile.size,
          s3Key: uploadedKey,
          sendEmail: getEmailNotificationSetting(),
          idempotencyKey,
          ...(overwriteConfirmedRef.current ? { allowOverwrite: true } : {})
        };

        if (formData.description && (typeof formData.description === 'object' ? formData.description.content_html?.trim() : formData.description.trim())) {
          contentData.description = typeof formData.description === 'object' ? formData.description.content_html : formData.description;
        }
        if (formData.week) contentData.week = formData.week;
        if (formData.recordingDate && formData.recordingDate.trim()) {
          const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
          contentData.recordingDate = chicagoMidnight ? chicagoMidnight.toUTC().toISO() : null;
        } else {
          contentData.recordingDate = null;
        }
      } else if (formData.file) {
        setUploadProgress({ percent: 0, loaded: 0, total: formData.file.size });
        // Use FormData for file uploads
        contentData = new FormData();
        contentData.append('type', getSingularTabName(activeTab));
        contentData.append('title', formData.title);
        contentData.append('priority', formData.priority);
        
        if (formData.description && (typeof formData.description === 'object' ? formData.description.content_html?.trim() : formData.description.trim())) {
          const descriptionHtml = typeof formData.description === 'object' ? formData.description.content_html : formData.description;
          contentData.append('description', descriptionHtml);
        }
        
        if (activeTab === 'recordings') {
          if (formData.week) contentData.append('week', formData.week);
          if (formData.recordingDate && formData.recordingDate.trim()) {
            const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
            if (chicagoMidnight) {
              const utcIso = chicagoMidnight.toUTC().toISO();
              if (utcIso) {
                contentData.append('recordingDate', utcIso);
              }
            }
          }
        }
        
        if (activeTab === 'syllabus') {
          // Syllabus is course-wide, no week needed
        }
        
        if (activeTab === 'slides') {
          if (formData.week) contentData.append('week', formData.week);
        }
        
        if (activeTab === 'readings') {
          if (formData.week) contentData.append('week', formData.week);
          // Handle multiple URLs for readings
          if (formData.urls && Array.isArray(formData.urls) && formData.urls.length > 0) {
            const validUrls = formData.urls.filter(urlItem => urlItem.url && urlItem.url.trim());
            if (validUrls.length > 0) {
              contentData.append('urls', JSON.stringify(validUrls));
            }
          }
        }
        
        if (formData.url && formData.url.trim()) {
          contentData.append('url', formData.url);
        }

        if (activeTab === 'slides' && slidesSourceType === 'upload') {
          contentData.delete('url');
        }

        if (activeTab === 'syllabus' && syllabusSourceType === 'upload') {
          contentData.delete('url');
        }

        contentData.append('sendEmail', getEmailNotificationSetting());
        contentData.append('idempotencyKey', idempotencyKey);
        contentData.append('file', formData.file);
        contentData.__onUploadProgress = setUploadProgress;
      } else {
        // Use regular object for non-file uploads
        contentData = {
          type: getSingularTabName(activeTab),
          title: formData.title,
          priority: formData.priority
        };

        if (formData.description && (typeof formData.description === 'object' ? formData.description.content_html?.trim() : formData.description.trim())) {
          const descriptionHtml = typeof formData.description === 'object' ? formData.description.content_html : formData.description;
          contentData.description = descriptionHtml;
        }

        if (activeTab === 'recordings') {
          if (formData.week) contentData.week = formData.week;
          // Always include recordingDate for recordings, even if null
          if (formData.recordingDate && formData.recordingDate.trim()) {
            const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
            contentData.recordingDate = chicagoMidnight ? chicagoMidnight.toUTC().toISO() : null;
          } else {
            contentData.recordingDate = null;
          }
        }

        if (activeTab === 'syllabus') {
          // Syllabus is course-wide, no week needed
        }

        if (activeTab === 'slides') {
          if (formData.week) contentData.week = formData.week;
        }

        if (activeTab === 'readings') {
          if (formData.week) contentData.week = formData.week;
          // Handle multiple URLs for readings
          if (formData.urls && Array.isArray(formData.urls) && formData.urls.length > 0) {
            const validUrls = formData.urls.filter(urlItem => urlItem.url && urlItem.url.trim());
            if (validUrls.length > 0) {
              contentData.urls = validUrls;
            }
          }
        }

        if (formData.url && formData.url.trim()) {
          contentData.url = formData.url;
        }

        if (activeTab === 'slides' && slidesSourceType === 'upload') {
          delete contentData.url;
        }

        if (activeTab === 'syllabus' && syllabusSourceType === 'upload') {
          delete contentData.url;
        }

        contentData.sendEmail = getEmailNotificationSetting();
        contentData.idempotencyKey = idempotencyKey;
      }

      const response = elective
        ? await apiService.addElectiveContent(elective.id, contentData)
        : await apiService.addCourseContent(course.id, contentData);
      
      if (response.success) {
        resetForm();
        fetchContent(); // Refresh content list
        onContentAdded && onContentAdded('added');
        onClose();
      } else {
        throw new Error(response.message || 'Failed to add content');
      }
    } catch (error) {
      // Server-side overwrite guard tripped: a recording already exists at the
      // destination key (the client list was stale / another session added it).
      // Surface the overwrite prompt instead of a generic error. Guarded by
      // overwriteConfirmedRef so a 409 even after confirming doesn't loop.
      if (error?.status === 409 && error?.data?.code === 'RECORDING_EXISTS' && !overwriteConfirmedRef.current) {
        openOverwriteModal({
          id: error.data.existingId,
          title: error.data.existingTitle,
          file_name: error.data.existingFileName,
        });
        return;
      }

      const info = uploadAbortRef.current;
      const isCancelled = error?.name === 'AbortError' || info?.cancelledByUser;
      // A dropped connection mid-upload should be cleaned up exactly like a
      // manual cancel: remove whatever already landed in the ingest bucket.
      const isNetworkError = error?.isNetworkError || error?.status === 0 ||
        /network error|failed to fetch|load failed/i.test(error?.message || '');

      if (info && (isCancelled || isNetworkError)) {
        await cleanupCancelledUpload();
      }

      if (isCancelled) {
        logger.info('Recording upload cancelled by user', { courseId: course?.id, electiveId: elective?.id });
        notify('warning', 'Upload canceled', 'The upload was canceled and any uploaded files were removed.');
      } else if (isNetworkError && info) {
        logger.warn('Recording upload interrupted by network error', { error: sanitizeErrorForLog(error), courseId: course?.id });
        notify('error', 'Connection lost', 'The upload was interrupted and any partially uploaded files were removed. Please try again.');
      } else {
        logger.error('Error adding content', { error: sanitizeErrorForLog(error), courseId: course?.id, electiveId: elective?.id, activeTab });
        notify('error', 'Add failed', `Failed to add content: ${sanitizeAwsSensitiveText(error.message)}`);
      }
    } finally {
      uploadAbortRef.current = null;
      overwriteConfirmedRef.current = false;
      setCancellingUpload(false);
      setLoading(false);
      setUploadProgress(null);
      submittingRef.current = false;
    }
  };

  const handleEditContent = (contentItem) => {
    
    setEditingContent(contentItem);
    
    // Keep the current tab (existing) when editing - form will show regardless
    // The form is shown when editingContent is set, regardless of sub-tab
    
    // Parse existing URLs if they exist
    let existingUrls = [];
    if (contentItem.urls) {
      try {
        // If urls is a string (JSON), parse it
        if (typeof contentItem.urls === 'string') {
          existingUrls = JSON.parse(contentItem.urls);
        } else if (Array.isArray(contentItem.urls)) {
          // If urls is already an array, use it directly
          existingUrls = contentItem.urls;
        }
        
        // Ensure each URL has an id field for React keys
        existingUrls = existingUrls.map((urlItem, index) => ({
          ...urlItem,
          id: urlItem.id || `existing-${index}-${Date.now()}`
        }));
        
      } catch (error) {
        logger.error('Error parsing URLs', { error, description: contentItem.description });
        existingUrls = [];
      }
    }
    
    // URLs are now stored separately in the urls field, no need to extract from description
    
    const chicagoDate = contentItem.recording_date ? (function() {
      try {
        // Handle the recording date conversion properly
        // The database stores dates as UTC timestamps, but for date-only fields like recording_date,
        // we should extract the date part directly from the ISO string to match how it's displayed
        // This matches the logic in VideoPlaylist.js and VideoPlayer.js which extract the date part directly
        
        let isoString;
        if (contentItem.recording_date instanceof Date) {
          isoString = contentItem.recording_date.toISOString();
        } else if (typeof contentItem.recording_date === 'string') {
          isoString = contentItem.recording_date;
        } else {
          logger.error('Unexpected recording_date type', { type: typeof contentItem.recording_date, value: contentItem.recording_date });
          return '';
        }
        
        // Extract the date part directly from the ISO string (YYYY-MM-DD)
        // This matches how the date is displayed in VideoPlaylist and VideoPlayer components
        // e.g., "2026-01-07T00:00:00.000Z" -> "2026-01-07"
        const datePart = isoString.slice(0, 10);
        if (datePart && datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return datePart;
        }
        
        // Fallback: if date extraction fails, try converting to Chicago timezone
        const chicagoDateTime = utcToChicago(isoString);
        if (chicagoDateTime.isValid) {
          return chicagoDateTime.toISODate();
        }
        
        logger.error('Invalid date format', { isoString });
        return '';
      } catch (e) {
        logger.error('Error converting recording date', { error: e, recording_date: contentItem.recording_date });
        return '';
      }
    })() : '';

    const newFormData = {
      title: contentItem.title || '',
      description: contentItem.description ? {
        content_json: null,
        content_html: contentItem.description,
        content_plain: contentItem.description.replace(/<[^>]*>/g, '').trim()
      } : { content_json: null, content_html: '', content_plain: '' },
      file: null,
      files: [],
      url: contentItem.url || '',
      urls: existingUrls, // Use existing URLs instead of empty array
      priority: contentItem.priority || 'medium',
      week: contentItem.week || '',
      recordingDate: chicagoDate
    };
    
    // Set video hosting type based on what exists
    if (contentItem.url && contentItem.url.trim()) {
      setVideoHostingType('external');
    } else {
      setVideoHostingType('upload');
    }

    if (contentItem.type === 'slide') {
      setSlidesSourceType(contentItem.file_path || contentItem.file_name ? 'upload' : 'external');
    }

    if (contentItem.type === 'syllabus') {
      setSyllabusSourceType(contentItem.file_path || contentItem.file_name ? 'upload' : 'external');
    }
    
    setFormData(newFormData);
  };

  const handleUpdateContent = async (e) => {
    e.preventDefault();
    if (submittingRef.current || loading) {
      logger.warn('Duplicate content update blocked on client', { contentId: editingContent?.id, activeTab });
      return;
    }

    if (!editingContent) return;

    submittingRef.current = true;


    setLoading(true);
    try {
      // Validate title
      if (!formData.title || !formData.title.trim()) {
        notify('warning', 'Title required', 'Please enter a title.');
        setLoading(false);
        submittingRef.current = false;
        return;
      }

      if (hasLocalFileUrl()) {
        notify('warning', 'Invalid link', 'Local file URLs are not allowed. Please upload the file or use a public https:// link.');
        setLoading(false);
        submittingRef.current = false;
        return;
      }

      // Validate recordings: must have either URL or file (existing or new)
      if (activeTab === 'recordings') {
        if (!videoHostingType) {
          notify('warning', 'Select hosting type', 'Please select a video hosting type (External or Upload).');
          setLoading(false);
          submittingRef.current = false;
          return;
        }

        if (videoHostingType === 'external') {
          const hasUrl = formData.url && formData.url.trim();
          if (!hasUrl) {
            notify('warning', 'Video URL required', 'Please provide a Video URL.');
            setLoading(false);
            submittingRef.current = false;
            return;
          }
        } else if (videoHostingType === 'upload') {
          // Allow update if there's a new file OR if existing content already has a file
          const hasNewFile = formData.file;
          const hasExistingFile = editingContent.file_path;
          if (!hasNewFile && !hasExistingFile) {
            notify('warning', 'File required', 'Please upload an MP4 file.');
            setLoading(false);
            submittingRef.current = false;
            return;
          }
        }
      }

      if (activeTab === 'syllabus') {
        const hasUrl = formData.url && formData.url.trim();
        const hasNewFile = formData.file;
        const hasExistingFile = editingContent.file_path || editingContent.file_name;

        if (syllabusSourceType === 'external' && !hasUrl) {
          notify('warning', 'Link required', 'Please provide a syllabus link URL.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }

        if (syllabusSourceType === 'upload' && !hasNewFile && !hasExistingFile) {
          notify('warning', 'File required', 'Please upload a syllabus file.');
          setLoading(false);
          submittingRef.current = false;
          return;
        }
      }

      // Extract description HTML - handle both TipTap format and legacy format
      let descriptionHtml = '';
      if (typeof formData.description === 'object') {
        descriptionHtml = formData.description.content_html || formData.description.html || '';
      } else if (typeof formData.description === 'string') {
        descriptionHtml = formData.description;
      }

      const contentData = {
        title: formData.title,
        priority: formData.priority,
        description: descriptionHtml,
        url: (activeTab === 'slides' && slidesSourceType === 'upload') || (activeTab === 'syllabus' && syllabusSourceType === 'upload') ? '' : formData.url || '',
        sendEmail: getEmailNotificationSetting()
      };


      // Include all fields for recordings, even if empty (to allow clearing)
      if (activeTab === 'recordings') {
        // Always include week field to allow clearing (null) or setting a value
        if (formData.week && String(formData.week).trim() !== '') {
          contentData.week = parseInt(formData.week, 10);
        } else {
          contentData.week = null; // Explicitly set to null to clear the field
        }
        if (formData.recordingDate) {
          const chicagoMidnight = createChicagoDateTime(formData.recordingDate, '00:00');
          // Force exact UTC midnight equivalence for the chosen wall date to avoid -1 day shifts when parsing elsewhere
          const utcIso = chicagoMidnight ? chicagoMidnight.toUTC().startOf('day').toISO() : null;
          contentData.recordingDate = utcIso;
        } else {
          contentData.recordingDate = null; // Explicitly set to null to clear the field
        }
      }

      if (activeTab === 'slides') {
        if (formData.week && String(formData.week).trim() !== '') {
          contentData.week = parseInt(formData.week, 10);
        } else {
          contentData.week = null;
        }
      }

      // Include URLs for readings
      if (activeTab === 'readings') {
        // Always include week field to allow clearing (null) or setting a value
        if (formData.week && String(formData.week).trim() !== '') {
          contentData.week = parseInt(formData.week, 10);
        } else {
          contentData.week = null; // Explicitly set to null to clear the field
        }
        // Handle multiple URLs for readings - always include URLs field to allow clearing
        if (formData.urls && Array.isArray(formData.urls) && formData.urls.length > 0) {
          const validUrls = formData.urls.filter(urlItem => urlItem.url && urlItem.url.trim());
          contentData.urls = validUrls; // Always set URLs, even if empty array
        } else {
          contentData.urls = []; // Explicitly set empty array to clear existing URLs
        }
      }


      const response = await apiService.updateCourseContent(editingContent.id, contentData);
      
      if (response.success) {
        resetForm();
        setEditingContent(null);
        fetchContent(); // Refresh content list
        onContentAdded && onContentAdded('updated');
      } else {
        throw new Error(response.message || 'Failed to update content');
      }
    } catch (error) {
      logger.error('Error updating content', { error, contentId: editingContent?.id, activeTab });
      notify('error', 'Update failed', `Failed to update content: ${error.message}`);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const performDeleteContent = async (contentId) => {
    setConfirmModal(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await apiService.deleteCourseContent(contentId);
      if (response.success) {
        closeConfirm();
        notify('success', 'Content deleted', 'The content has been removed.');
        fetchContent(); // Refresh content list
        // Refresh the parent course/elective page so its sections update too.
        onContentAdded && onContentAdded('deleted');
      } else {
        throw new Error(response.message || 'Failed to delete content');
      }
    } catch (error) {
      logger.error('Error deleting content', { error, contentId });
      closeConfirm();
      notify('error', 'Delete failed', `Failed to delete content: ${error.message}`);
    }
  };

  const handleDeleteContent = (item) => {
    // `item` may be the full content object (recordings) or just an id (other
    // content types still call with an id).
    const contentId = typeof item === 'object' && item !== null ? item.id : item;
    const isRecording = (typeof item === 'object' && item?.type === 'recording') || activeTab === 'recordings';

    if (isRecording) {
      openDeleteRecordingModal(item, contentId);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Delete content',
      message: 'Are you sure you want to delete this content? This action cannot be undone.',
      confirmText: 'Delete',
      type: 'danger',
      isLoading: false,
      onConfirm: () => performDeleteContent(contentId)
    });
  };

  // Recording delete: open the typed-confirmation modal and load the list of
  // ingest/serving bucket files that will be removed.
  const openDeleteRecordingModal = async (item, contentId) => {
    const recordingId = contentId ?? (typeof item === 'object' ? item?.id : item);
    setDeleteRecordingModal({
      isOpen: true,
      loading: true,
      deleting: false,
      contentId: recordingId,
      recording: typeof item === 'object' && item ? { id: item.id, title: item.title, file_name: item.file_name } : null,
      serving: null,
      ingest: null,
      confirmText: '',
      error: null,
    });
    try {
      const res = await apiService.getRecordingDeleteFiles(recordingId);
      if (res?.success) {
        setDeleteRecordingModal(prev => prev.contentId === recordingId ? {
          ...prev,
          loading: false,
          recording: res.recording || prev.recording,
          serving: res.serving || { bucket: null, files: [] },
          ingest: res.ingest || { bucket: null, files: [] },
        } : prev);
      } else {
        throw new Error(res?.message || 'Failed to load recording files');
      }
    } catch (error) {
      logger.error('Error loading recording files for delete', { error: sanitizeErrorForLog(error), contentId: recordingId });
      setDeleteRecordingModal(prev => prev.contentId === recordingId ? {
        ...prev,
        loading: false,
        error: sanitizeAwsSensitiveText(error?.message || 'Failed to load recording files'),
      } : prev);
    }
  };

  const closeDeleteRecordingModal = () => {
    setDeleteRecordingModal(prev => prev.deleting ? prev : { ...prev, isOpen: false });
  };

  const performDeleteRecording = async () => {
    const { contentId, confirmText } = deleteRecordingModal;
    if (!contentId || !/^delete$/i.test((confirmText || '').trim())) return;
    setDeleteRecordingModal(prev => ({ ...prev, deleting: true, error: null }));
    try {
      const response = await apiService.deleteCourseContent(contentId, { confirm: confirmText.trim() });
      if (response?.success) {
        setDeleteRecordingModal(prev => ({ ...prev, isOpen: false, deleting: false }));
        notify('success', 'Recording deleted', 'Recordings were successfully deleted for this session.');
        fetchContent();
        // Refresh the course/elective page so the playlist drops the recording.
        onContentAdded && onContentAdded('deleted');
      } else {
        throw new Error(response?.message || 'Failed to delete recording');
      }
    } catch (error) {
      logger.error('Error deleting recording', { error: sanitizeErrorForLog(error), contentId });
      setDeleteRecordingModal(prev => ({ ...prev, deleting: false, error: sanitizeAwsSensitiveText(error?.message || 'Failed to delete recording') }));
    }
  };

  // True if a recording's stored name matches the pending upload's final S3 file
  // name (date-derived) — i.e. it occupies the destination key.
  const recordingMatchesTarget = (rec, targetLower) => {
    const name = rec.file_name || (rec.file_path ? rec.file_path.split('/').pop() : '');
    return !!name && name.toLowerCase() === targetLower;
  };

  // Find an existing (course) recording whose final S3 file name matches what the
  // pending upload would produce — i.e. an overwrite collision.
  const findCollidingRecording = () => {
    const targetName = recordingFinalFileName(formData.recordingDate);
    if (!targetName) return null;
    const target = targetName.toLowerCase();
    return (content.recordings || []).find(rec => recordingMatchesTarget(rec, target)) || null;
  };

  // Collect the ids of ALL recordings that collide at the destination key, not
  // just the one shown in the prompt — duplicate rows can pre-exist (e.g. two
  // same-date uploads from before this feature) and must all be removed. A fresh
  // fetch also catches rows the (possibly stale) modal list missed.
  const collectCollidingRecordingIds = async (primaryId) => {
    const ids = new Set();
    if (primaryId) ids.add(primaryId);
    const targetName = recordingFinalFileName(formData.recordingDate);
    if (targetName && course?.id) {
      const target = targetName.toLowerCase();
      try {
        const res = await apiService.getCourseContent(course.id, { includeProcessing: true });
        const recs = res?.success ? (res.content?.recordings || []) : [];
        recs.forEach(rec => { if (recordingMatchesTarget(rec, target)) ids.add(rec.id); });
      } catch (error) {
        // Fall back to just the primary id — better to remove one than none.
        logger.warn('Failed to refresh recordings for overwrite; deleting primary only', { error: sanitizeErrorForLog(error) });
      }
    }
    return [...ids];
  };

  // Open the overwrite prompt and load the file list for the recording that
  // would be replaced (reuses the delete-files endpoint).
  // When the uploader picks an existing session (or types a date/week) that
  // already has a recording, proactively open the overwrite prompt so they can
  // clear the slot before uploading. Best-effort off the loaded list; the
  // submit-time check (client + server 409) remains the backstop.
  const maybePromptOverwriteForDate = (dateStr) => {
    if (loading || uploadProgress || overwriteModal.isOpen || deleteRecordingModal.isOpen) return;
    const targetName = recordingFinalFileName(dateStr);
    if (!targetName) return;
    const target = targetName.toLowerCase();
    const existing = (content.recordings || []).find(rec => recordingMatchesTarget(rec, target));
    if (existing) {
      openOverwriteModal(existing, 'clear');
    }
  };

  const openOverwriteModal = async (existing, mode = 'upload') => {
    // How many existing recordings sit on the destination key (from the loaded
    // list) — used to tell the user when more than one will be removed.
    const targetName = recordingFinalFileName(formData.recordingDate);
    const target = targetName ? targetName.toLowerCase() : null;
    const collidingCount = Math.max(
      1,
      target ? (content.recordings || []).filter(rec => recordingMatchesTarget(rec, target)).length : 1
    );
    setOverwriteModal({
      isOpen: true,
      loading: true,
      deleting: false,
      contentId: existing.id,
      recording: { id: existing.id, title: existing.title, file_name: existing.file_name },
      serving: null,
      ingest: null,
      confirmText: '',
      error: null,
      collidingCount,
      mode,
    });
    try {
      const res = await apiService.getRecordingDeleteFiles(existing.id);
      if (res?.success) {
        setOverwriteModal(prev => prev.contentId === existing.id ? {
          ...prev,
          loading: false,
          recording: res.recording || prev.recording,
          serving: res.serving || { bucket: null, files: [] },
          ingest: res.ingest || { bucket: null, files: [] },
        } : prev);
      } else {
        throw new Error(res?.message || 'Failed to load recording files');
      }
    } catch (error) {
      logger.error('Error loading recording files for overwrite', { error: sanitizeErrorForLog(error), contentId: existing.id });
      setOverwriteModal(prev => prev.contentId === existing.id ? {
        ...prev,
        loading: false,
        error: sanitizeAwsSensitiveText(error?.message || 'Failed to load recording files'),
      } : prev);
    }
  };

  const closeOverwriteModal = () => {
    if (overwriteModal.deleting) return;
    // Cancelling a proactive ('clear') prompt means the chosen date collides and
    // the user declined to overwrite — clear the week/date and the session
    // selection so they're back to a clean slate and can pick another, while
    // keeping them on the New form (blanking the date would otherwise let the
    // auto-switch flip to the Existing tab).
    if (overwriteModal.mode === 'clear') {
      keepRecordingsNewTabRef.current = true;
      setRecordingsSubTab('new');
      setFormData(prev => ({ ...prev, week: '', recordingDate: '' }));
      // Stay in "different week & date" if that's where they were (keep the
      // inputs visible, just blanked); otherwise reset the session dropdown.
      setSelectedAttendanceSessionId(prev => prev === 'custom' ? 'custom' : '');
    }
    setOverwriteModal(prev => ({ ...prev, isOpen: false }));
  };

  // Confirm overwrite: delete EVERY recording at the destination key + all their
  // files/rows (same path as a manual delete). In 'upload' mode (collision found
  // at submit) it then re-runs the upload; in 'clear' mode (user pre-selected an
  // existing session/date) it stops after clearing so they can upload normally.
  const performOverwriteAndUpload = async () => {
    const { contentId, confirmText, mode } = overwriteModal;
    if (!contentId || !/^overwrite$/i.test((confirmText || '').trim())) return;
    setOverwriteModal(prev => ({ ...prev, deleting: true, error: null }));
    try {
      const collidingIds = await collectCollidingRecordingIds(contentId);
      for (const id of collidingIds) {
        const response = await apiService.deleteCourseContent(id, { confirm: 'DELETE' });
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to remove an existing recording');
        }
      }
      setOverwriteModal(prev => ({ ...prev, isOpen: false, deleting: false }));
      onContentAdded && onContentAdded('deleted');
      // Refresh the modal's own list so a follow-up submit doesn't re-detect the
      // recording we just removed.
      if (typeof fetchContent === 'function') fetchContent();

      if (mode === 'clear') {
        // Slot cleared up front — the user uploads their file next.
        notify('success', 'Existing recording removed', 'The recording for this date was removed. Upload your file to replace it.');
        return;
      }

      overwriteConfirmedRef.current = true;
      try {
        await handleSubmit({ preventDefault: () => {} });
      } finally {
        // handleSubmit clears this in its own finally, but if it bails at an
        // early guard (before its try) the flag would leak true and wrongly skip
        // the NEXT upload's overwrite check. Always clear it here too.
        overwriteConfirmedRef.current = false;
      }
    } catch (error) {
      logger.error('Error overwriting recording', { error: sanitizeErrorForLog(error), contentId });
      overwriteConfirmedRef.current = false;
      setOverwriteModal(prev => ({ ...prev, deleting: false, error: sanitizeAwsSensitiveText(error?.message || 'Failed to overwrite recording') }));
    }
  };

  const renderForm = () => {
    const safeTab = typeof activeTab === 'string' ? activeTab : 'recordings';
    const currentContent = content[safeTab] || [];
    
    // For all content types, show different content based on sub-tab
    const getCurrentSubTab = () => {
      switch (activeTab) {
        case 'recordings': return recordingsSubTab;
        case 'announcements': return announcementsSubTab;
        case 'syllabus': return syllabusSubTab;
        case 'slides': return slidesSubTab;
        case 'readings': return readingsSubTab;
        default: return 'new';
      }
    };

    const currentSubTab = getCurrentSubTab();
    
    // Show existing items list only if NOT editing and on existing tab
    if (currentSubTab === 'existing' && !editingContent) {
      return (
        <div>
          {activeTab === 'recordings' && course?.id && currentContent.length >= 2 && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '0.875rem',
            }}>
              <button
                type="button"
                onClick={() => setShowMergeModal(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4375rem',
                  padding: '0.5rem 0.875rem',
                  background: 'transparent',
                  color: 'var(--primary-color)',
                  border: '1px solid var(--primary-color)',
                  borderRadius: '8px',
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                title="Merge multiple recordings into one"
              >
                <FiLayers size={14} />
                Merge recordings
              </button>
            </div>
          )}
          <ContentList>
            {currentContent.length > 0 ? (
            currentContent.map((item, index) => {
              // Get icon based on content type
              const getIcon = () => {
                switch (activeTab) {
                  case 'recordings': return <FiVolume2 size={20} />;
                  case 'announcements': return <FiAlertCircle size={20} />;
                  case 'syllabus': return <FiBookOpen size={20} />;
                  case 'slides': return <FiFileText size={20} />;
                  case 'readings': return <FiBookOpen size={20} />;
                  default: return null;
                }
              };

              // Build title with week prefix if applicable
              const getTitle = () => {
                const weekPrefix = (activeTab === 'recordings' || activeTab === 'slides' || activeTab === 'readings') && item.week 
                  ? `Week ${item.week}: ` 
                  : '';
                return `${weekPrefix}${item.title}`;
              };

              // Build secondary meta (author + date)
              const getSecondaryMeta = () => {
                const parts = [];
                if (item.creator_first_name && item.creator_last_name) {
                  parts.push(`${item.creator_first_name} ${item.creator_last_name}`);
                }
                parts.push(`Posted: ${formatDateForDisplay(item.created_at, 'M/d/yyyy h:mm a')}`);
                return parts.join(' • ');
              };

              const uploadedFileName = item.file_name || (item.file_path ? item.file_path.split('/').pop() : 'File');
              const existingFileUrl = getExistingFileUrl(item);
              const externalRecordingUrl = activeTab === 'recordings' && typeof item.url === 'string' ? item.url.trim() : '';

              // Build file details
              const getFileDetails = () => {
                const parts = [];
                if (activeTab === 'recordings' && item.recording_date) {
                  parts.push(`Recorded: ${formatDateForDisplay(item.recording_date, 'M/d/yyyy')}`);
                }
                if (activeTab === 'recordings' && item.duration) {
                  parts.push(`Duration: ${item.duration}`);
                }
                // Show file info if file_name or file_path exists
                if (item.file_name || item.file_path) {
                  parts.push(`${activeTab === 'slides' ? 'Uploaded file' : 'Filename'}: ${uploadedFileName}`);
                }
                if (item.file_size) {
                  parts.push(`Size: ${formatRecordingFileSize(item.file_size)}`);
                }
                return parts.length > 0 ? parts.join(' • ') : null;
              };

              return (
                <div key={index} className={styles.resourceCard}>
                  <div className={styles.resourceCardContent}>
                    {/* Zone 1: Icon + Title */}
                    <div className={styles.resourceCardHeader}>
                      <div className={styles.resourceCardIcon}>
                        {getIcon()}
                      </div>
                      <h3 className={styles.resourceCardTitle}>
                        {getTitle()}
                      </h3>
                    </div>

                    {/* Zone 2: Secondary Meta (Author + Date) */}
                    <div className={styles.resourceCardMeta}>
                      {getSecondaryMeta()}
                    </div>

                    {/* Zone 3: File Details (if exists) */}
                    {getFileDetails() && (
                      <div className={styles.resourceCardFileDetails}>
                        {activeTab === 'slides' && (item.file_name || item.file_path) && (
                          <span className={styles.uploadedFileBadge}>
                            <FiUpload size={12} />
                            File uploaded
                          </span>
                        )}
                        {getFileDetails()}
                      </div>
                    )}

                    {activeTab === 'recordings' && (externalRecordingUrl || item.id) && (
                      <div className={styles.resourceCardLinkDetails}>
                        {externalRecordingUrl && (
                          <>
                            <a
                              className={styles.externalLinkBadge}
                              href={externalRecordingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FiExternalLink size={12} />
                              Open link
                            </a>
                            <button
                              type="button"
                              className={styles.externalLinkBadge}
                              title="Copy direct video link"
                              onClick={() => copyTextToClipboard(externalRecordingUrl, 'Recording link copied to clipboard.')}
                            >
                              <FiCopy size={12} />
                              Copy link
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className={styles.externalLinkBadge}
                          title="Check MediaConvert job status"
                          onClick={() => fetchMergeStatus(item.id, `${item.week ? `Week ${item.week} - ` : ''}${item.title}`)}
                        >
                          <FiActivity size={12} />
                          Check recording status
                        </button>
                      </div>
                    )}

                    {/* External-URL slides: surface the link so it can be opened/viewed. */}
                    {activeTab === 'slides' && typeof item.url === 'string' && item.url.trim() && (
                      <div className={styles.resourceCardLinkDetails}>
                        <a
                          className={styles.externalLinkBadge}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FiExternalLink size={12} />
                          View
                        </a>
                      </div>
                    )}

                    {/* Special handling for readings with URLs */}
                    {activeTab === 'readings' && item.urls && item.urls.length > 0 && (
                      <div style={{ marginTop: '12px', marginLeft: '32px' }}>
                        <div style={{ 
                          display: 'flex', 
                          gap: '8px', 
                          flexWrap: 'wrap' 
                        }}>
                          {item.urls.map((urlItem, urlIndex) => (
                            <a
                              key={urlIndex}
                              href={urlItem.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                fontSize: '12px', 
                                padding: '6px 12px',
                                color: 'var(--text-muted)',
                                textDecoration: 'none',
                                border: '1px solid rgba(15, 23, 42, 0.1)',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease',
                                fontFamily: 'Inter, sans-serif'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--primary-color)';
                                e.currentTarget.style.color = 'var(--primary-color)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'rgba(15, 23, 42, 0.1)';
                                e.currentTarget.style.color = 'var(--text-muted)';
                              }}
                            >
                              <FiExternalLink size={12} />
                              {urlItem.label || `Link ${urlIndex + 1}`}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions: View/Download (primary), Edit (secondary), Delete (danger) */}
                  <div className={styles.resourceCardActions}>
                    {/* View is only kept for viewable slides — for recordings it
                        duplicates the "Open link" action, so it's omitted there. */}
                    {activeTab === 'slides' && existingFileUrl && isViewableFile(uploadedFileName) && (
                      <Tooltip content="View" position="bottom" className={styles.resourceCardActionTooltip}>
                        <a
                          className={`${styles.resourceCardActionButton} ${styles.primary}`}
                          title="View"
                          href={existingFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View"
                        >
                          <FiEye size={16} />
                        </a>
                      </Tooltip>
                    )}
                    {/* Download - Primary, always visible if file exists */}
                    {((activeTab === 'recordings' && (externalRecordingUrl || item.file_path || item.file_name)) || ((activeTab === 'syllabus' || activeTab === 'slides' || activeTab === 'readings') && (item.file_path || item.file_name))) && (
                      <Tooltip content="Download" position="bottom" className={styles.resourceCardActionTooltip}>
                        <button
                          className={`${styles.resourceCardActionButton} ${styles.primary}`}
                          title="Download"
                          onClick={() => {
                            const downloadUrl = existingFileUrl || externalRecordingUrl;
                            if (downloadUrl) {
                              window.open(activeTab === 'recordings' ? getDownloadUrl(downloadUrl, uploadedFileName) : downloadUrl, '_blank');
                            }
                          }}
                        >
                          <FiDownload size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {/* Edit - Secondary, appears on hover (desktop) */}
                    <Tooltip content="Edit" position="bottom" className={styles.resourceCardActionTooltip}>
                      <button
                        className={`${styles.resourceCardActionButton} ${styles.secondary}`}
                        title="Edit"
                        onClick={() => handleEditContent(item)}
                      >
                        <FiEdit size={16} />
                      </button>
                    </Tooltip>
                    {/* Delete - Danger, separated slightly, appears on hover (desktop) */}
                    <Tooltip content="Delete" position="bottom" className={styles.resourceCardActionTooltip}>
                      <button
                        className={`${styles.resourceCardActionButton} ${styles.delete}`}
                        title="Delete"
                        onClick={() => handleDeleteContent(item)}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState>
              No {safeTab} available yet. Add your first {getSingularTabName(activeTab)} below.
            </EmptyState>
          )}
        </ContentList>
        </div>
      );
    }
    
    // For readings new tab or other tabs, show the form
    return (
      <>
        {/* Show form for new sub-tab OR when editing existing content, but not for notifications tab */}
        {(currentSubTab === 'new' || editingContent) && activeTab !== 'notifications' && (
          <form onSubmit={editingContent ? handleUpdateContent : handleSubmit}>
            {/* Basics Section */}
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionHeaderText}>Basics</span>
              </div>
              <div className={styles.formGroup}>
                <label className={`${styles.label} ${styles.labelRequired}`}>Title</label>
                <input
                  className={styles.input}
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder={`Enter ${getSingularTabName(activeTab)} title`}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Description</label>
                {(activeTab === 'announcements' || activeTab === 'readings') ? (
                  <RichTextEditorTipTap
                    value={formData.description}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, description: value }));
                    }}
                    placeholder={`Enter ${getSingularTabName(activeTab)} description`}
                    enableImages
                  />
                ) : (
                  <textarea
                    className={styles.textarea}
                    name="description"
                    value={typeof formData.description === 'object' ? formData.description.content_plain || '' : formData.description || ''}
                    onChange={handleInputChange}
                    placeholder={`Enter ${getSingularTabName(activeTab)} description`}
                  />
                )}
              </div>
            </div>

          {activeTab === 'recordings' && (
            <>
              {/* Scheduling Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Scheduling</span>
                </div>

                {attendanceSessionOptions.length > 0 && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Select Existing Sessions</label>
                    <select
                      className={styles.input}
                      value={selectedAttendanceSessionId}
                      onChange={handleAttendanceSessionSelect}
                    >
                      <option value="">Choose Session</option>
                      {attendanceSessionOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                      <option value="custom">Use a different week & date</option>
                    </select>
                  </div>
                )}

                {(attendanceSessionOptions.length === 0 || selectedAttendanceSessionId === 'custom') && (
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label className={`${styles.label} ${styles.labelRequired}`}>Week Number</label>
                      <input
                        className={styles.input}
                        name="week"
                        type="text"
                        inputMode="numeric"
                        value={formData.week}
                        onChange={handleInputChange}
                        placeholder="Enter week number"
                        required
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={`${styles.label} ${styles.labelRequired}`}>Recording Date</label>
                      <input
                        className={styles.input}
                        name="recordingDate"
                        type="date"
                        value={formData.recordingDate}
                        required
                        onClick={openNativeDatePicker}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData(prev => ({ ...prev, recordingDate: v }));
                          // Prompt to overwrite if this date already has a recording.
                          maybePromptOverwriteForDate(v);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Media Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Media</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.labelRequired}`}>Video Hosting</label>
                  <div className={styles.radioGroup}>
                    <label className={`${styles.radioPill} ${videoHostingType === 'upload' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="videoHostingType"
                        value="upload"
                        checked={videoHostingType === 'upload'}
                        onChange={(e) => {
                          setVideoHostingType('upload');
                          setFormData(prev => ({ ...prev, url: '' }));
                        }}
                      />
                      Upload
                    </label>
                    <label className={`${styles.radioPill} ${videoHostingType === 'external' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="videoHostingType"
                        value="external"
                        checked={videoHostingType === 'external'}
                        onChange={(e) => {
                          setVideoHostingType('external');
                          setFormData(prev => ({ ...prev, file: null, files: [] }));
                          const fileInput = document.getElementById('recordings-file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                      />
                      External
                    </label>
                  </div>
                </div>

                {videoHostingType === 'external' && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Video URL
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            url: 'https://d8eiitau7n3ii.cloudfront.net/'
                          }));
                        }}
                        style={{
                          marginLeft: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'var(--primary-color)',
                          backgroundColor: 'rgba(216, 184, 137, 0.1)',
                          border: '1px solid rgba(216, 184, 137, 0.2)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Paste Host
                      </button>
                    </label>
                    <input
                      className={styles.input}
                      name="url"
                      type="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      placeholder="https://your-server.com/videos/week1.mp4"
                    />
                  </div>
                )}

                {videoHostingType === 'upload' && (
                  <>
                    <FileUploadField
                      id="recordings-file-input"
                      label="VIDEO UPLOAD"
                      optional={false}
                      accept=".mp4,video/mp4"
                      helperText=""
                      value={formData.files?.length ? formData.files : formData.file}
                      multiple={!editingContent && !elective}
                      maxSize={MAX_RECORDING_UPLOAD_BYTES}
                      onChange={(selection) => {
                        const selectedFiles = Array.isArray(selection) ? selection : (selection ? [selection] : []);
                        setFormData(prev => {
                          const existingFiles = prev.files?.length ? prev.files : (prev.file ? [prev.file] : []);
                          const existingKeys = new Set(existingFiles.map(file => `${file.name}:${file.size}:${file.lastModified}`));
                          const additions = selectedFiles.filter(file => !existingKeys.has(`${file.name}:${file.size}:${file.lastModified}`));
                          const files = existingFiles.length > 0
                            ? [...existingFiles, ...additions]
                            : sortFilesByLastModified(additions);
                          return { ...prev, file: files[0] || null, files };
                        });
                      }}
                      onClear={() => setRecordingFiles([])}
                      existingFileName={getExistingFileName()}
                      existingFileUrl={getExistingFileUrl()}
                      onSizeExceeded={() => notify('warning', 'File too large', 'Recording files cannot exceed 5 TB.')}
                    />
                  {!editingContent && !elective && formData.files?.length > 1 && (
                    <div className={styles.mergeOrderPanel}>
                      <div className={styles.mergeOrderHeader}>
                        <div>
                          <div className={styles.mergeOrderTitle}>File Merge</div>
                          <div className={styles.mergeOrderHint}>Files are sorted by timestamp. Use ↑ and ↓ to reorder files for merging and × to remove file.</div>
                        </div>
                        <button type="button" className={styles.mergeOrderSortButton} onClick={sortRecordingFilesByTimestamp}>
                          Sort by timestamp
                        </button>
                      </div>
                      <ol className={styles.mergeOrderList}>
                        {formData.files.map((file, index) => (
                          <li key={`${file.name}-${file.size}-${file.lastModified}-${index}`} className={styles.mergeOrderItem}>
                            <span className={styles.mergeOrderIndex}>{index + 1}</span>
                            <div className={styles.mergeOrderMeta}>
                              <div className={styles.mergeOrderFileName}>{file.name}</div>
                              <div className={styles.mergeOrderFileInfo}>{formatFileTimestamp(file.lastModified)} · {formatFileSize(file.size)}</div>
                            </div>
                            <div className={styles.mergeOrderActions}>
                              <button type="button" onClick={() => moveRecordingFile(index, -1)} disabled={index === 0 || loading} title="Move up">
                                ↑
                              </button>
                              <button type="button" onClick={() => moveRecordingFile(index, 1)} disabled={index === formData.files.length - 1 || loading} title="Move down">
                                ↓
                              </button>
                              <button type="button" onClick={() => removeRecordingFile(index)} disabled={loading} title="Remove file">
                                ×
                              </button>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  </>
                )}
              </div>
            </>
          )}

          {activeTab === 'slides' && (
            <>
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Slide Source</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.labelRequired}`}>Choose One</label>
                  <div className={styles.radioGroup}>
                    <label className={`${styles.radioPill} ${slidesSourceType === 'upload' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="slidesSourceType"
                        value="upload"
                        checked={slidesSourceType === 'upload'}
                        onChange={() => {
                          setSlidesSourceType('upload');
                          setFormData(prev => ({ ...prev, url: '' }));
                        }}
                      />
                      Upload File
                    </label>
                    <label className={`${styles.radioPill} ${slidesSourceType === 'external' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="slidesSourceType"
                        value="external"
                        checked={slidesSourceType === 'external'}
                        onChange={() => {
                          setSlidesSourceType('external');
                          setFormData(prev => ({ ...prev, file: null }));
                          const fileInput = document.getElementById('file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                      />
                      External Link
                    </label>
                  </div>
                </div>

                {slidesSourceType === 'upload' && (
                  <FileUploadField
                    id="file-input"
                    label="Document File"
                    optional={false}
                    accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    helperText="Accepted formats: PDF, HTML, DOC, DOCX, XLS, XLSX, PPT, PPTX"
                    value={formData.file}
                    onChange={(file) => setFormData(prev => ({ ...prev, file }))}
                    onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                    existingFileName={getExistingFileName()}
                    existingFileUrl={getExistingFileUrl()}
                  />
                )}

                {slidesSourceType === 'external' && (
                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.labelRequired}`}>Link URL</label>
                    <input
                      className={styles.input}
                      name="url"
                      type="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      placeholder=""
                    />
                  </div>
                )}
              </div>

              {/* Week Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Week</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Week (Optional)</label>
                  <input
                    className={styles.input}
                    name="week"
                    type="text"
                    inputMode="numeric"
                    value={formData.week}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'readings' && (
            <>
              {/* Reading URLs Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Reading URLs</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Reading URLs (Optional)</label>
                  <div style={{ 
                    marginBottom: '1rem',
                    padding: '1rem',
                    border: '1px solid rgba(15, 23, 42, 0.1)',
                    borderRadius: '12px',
                    backgroundColor: 'white'
                  }}>
                  {(formData.urls || []).map((urlItem, index) => (
                    <div key={urlItem.id} style={{ 
                      position: 'relative',
                      marginBottom: '1rem',
                      padding: '1rem',
                      border: '1px solid rgba(15, 23, 42, 0.12)',
                      borderRadius: '12px',
                      backgroundColor: 'white'
                    }}>
                      <ActionButton
                        type="button"
                        className="danger"
                        size="sm"
                        onClick={() => removeUrl(urlItem.id)}
                        style={{ 
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          padding: '0.5rem',
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                      >
                        <FiX size={14} />
                      </ActionButton>
                      <div style={{ 
                        paddingRight: '2.5rem' // Make space for the X button
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '0.75rem',
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            backgroundColor: 'var(--primary-color)',
                            color: 'var(--text-inverse)',
                            borderRadius: 0,
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          <label style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: 'var(--text-secondary)',
                            margin: 0
                          }}>
                            Link Label
                          </label>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem',
                          marginBottom: '0.75rem'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '40px', // Match input height
                            flexShrink: 0
                          }}>
                            {/* Invisible spacer to align with the numbered badge above */}
                          </div>
                          <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g., 'Main Article', 'PDF Download'"
                            value={urlItem.label}
                            onChange={(e) => updateUrl(urlItem.id, 'label', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                              // Prevent any key from bubbling up and potentially closing the modal
                              e.stopPropagation();
                            }}
                            style={{ 
                              flex: 1
                            }}
                          />
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '0.25rem',
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            flexShrink: 0
                          }}>
                            {/* Invisible spacer to align with the numbered badge above */}
                          </div>
                          <label style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: '600', 
                            color: 'var(--text-secondary)',
                            margin: 0
                          }}>
                            Link URL
                          </label>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '40px', // Match input height
                            flexShrink: 0
                          }}>
                            {/* Invisible spacer to align with the numbered badge above */}
                          </div>
                          <input
                            className={styles.input}
                            type="url"
                            placeholder="https://example.com/article.pdf"
                            value={urlItem.url}
                            onChange={(e) => updateUrl(urlItem.id, 'url', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                              // Prevent any key from bubbling up and potentially closing the modal
                              e.stopPropagation();
                            }}
                            style={{ 
                              flex: 1
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <ActionButton
                    type="button"
                    className="secondary"
                    size="sm"
                    onClick={addUrl}
                    style={{ 
                      marginTop: '0.5rem',
                      padding: '0.75rem 1rem'
                    }}
                  >
                    <FiPlus size={16} />
                    Add URL
                  </ActionButton>
                </div>
                <small className={styles.helperText}>
                  Add multiple reading URLs with custom labels for better organization
                </small>
                </div>
              </div>

              {/* Document File Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Document File</span>
                </div>
                <FileUploadField
                  id="readings-file-input"
                  label="Document File"
                  optional={true}
                  accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  helperText="Accepted formats: PDF, HTML, DOC, DOCX, XLS, XLSX, PPT, PPTX"
                  value={formData.file}
                  onChange={(file) => setFormData(prev => ({ ...prev, file }))}
                  onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                  existingFileName={getExistingFileName()}
                  existingFileUrl={getExistingFileUrl()}
                />
              </div>

              {/* Week Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Week</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Week (Optional)</label>
                  <input
                    className={styles.input}
                    name="week"
                    type="text"
                    inputMode="numeric"
                    value={formData.week}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'syllabus' && (
            <>
              {/* Syllabus Source Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Syllabus Source</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.labelRequired}`}>Choose One</label>
                  <div className={styles.radioGroup}>
                    <label className={`${styles.radioPill} ${syllabusSourceType === 'upload' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="syllabusSourceType"
                        value="upload"
                        checked={syllabusSourceType === 'upload'}
                        onChange={() => {
                          setSyllabusSourceType('upload');
                          setFormData(prev => ({ ...prev, url: '' }));
                        }}
                      />
                      Upload File
                    </label>
                    <label className={`${styles.radioPill} ${syllabusSourceType === 'external' ? styles.active : ''}`}>
                      <input
                        type="radio"
                        name="syllabusSourceType"
                        value="external"
                        checked={syllabusSourceType === 'external'}
                        onChange={() => {
                          setSyllabusSourceType('external');
                          setFormData(prev => ({ ...prev, file: null }));
                          const fileInput = document.getElementById('syllabus-file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                      />
                      External Link
                    </label>
                  </div>
                </div>

                {syllabusSourceType === 'upload' && (
                  <FileUploadField
                    id="syllabus-file-input"
                    label="Document File"
                    optional={false}
                    accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    helperText="Accepted formats: PDF, HTML, DOC, DOCX, XLS, XLSX, PPT, PPTX"
                    value={formData.file}
                    onChange={(file) => setFormData(prev => ({ ...prev, file, url: '' }))}
                    onClear={() => setFormData(prev => ({ ...prev, file: null }))}
                    existingFileName={getExistingFileName()}
                    existingFileUrl={getExistingFileUrl()}
                  />
                )}

                {syllabusSourceType === 'external' && (
                  <div className={styles.formGroup}>
                    <label className={`${styles.label} ${styles.labelRequired}`}>Link URL</label>
                    <input
                      className={styles.input}
                      name="url"
                      type="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      placeholder=""
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'announcements' && (
            <>
              {/* Priority Section */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionHeaderText}>Priority</span>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Priority</label>
                  <select
                    className={styles.input}
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </>
          )}

            {/* Inline Footer Actions */}
            {uploadProgress && (
              <div className={styles.uploadProgressCard} role="status" aria-live="polite">
                <div className={styles.uploadProgressHeader}>
                  <span>
                    {uploadProgress.fileName && uploadProgress.currentIndex && uploadProgress.totalCount
                      ? `${uploadProgress.currentIndex}/${uploadProgress.totalCount}: Uploading file (${uploadProgress.fileName}) in progress.`
                      : 'Uploading file in progress.'} Please keep this page open until upload is complete.
                  </span>
                  <span>{uploadProgress.percent}%</span>
                </div>
                <div className={styles.uploadProgressTrack} aria-hidden="true">
                  <div
                    className={styles.uploadProgressBar}
                    style={{ width: `${uploadProgress.percent}%` }}
                  />
                </div>
                {formatUploadEta(uploadEtaSeconds) && (
                  <div className={styles.uploadProgressEta}>
                    Estimated time remaining: {formatUploadEta(uploadEtaSeconds)}
                  </div>
                )}
                {uploadProgress.total > 0 && (
                  <div className={styles.uploadProgressMeta}>
                    {formatUploadBytes(uploadProgress.loaded)} of {formatUploadBytes(uploadProgress.total)}
                  </div>
                )}
                {!elective && activeTab === 'recordings' && videoHostingType === 'upload' && !editingContent && (
                  <div className={styles.uploadProgressActions}>
                    <button
                      type="button"
                      className={styles.uploadCancelButton}
                      onClick={handleCancelUpload}
                      disabled={cancellingUpload}
                    >
                      {cancellingUpload ? 'Canceling…' : 'Cancel upload'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className={styles.inlineFooter}>
              <div className={styles.footerButtons}>
                {editingContent ? (
                  <>
                    <button 
                      type="button" 
                      className={`${styles.button} ${styles.buttonSecondary}`}
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : `Update ${getSingularTabName(activeTab)}`}
                    </button>
                  </>
                ) : (
                  <>
                    {onClose && (
                      <button 
                        type="button" 
                        className={`${styles.button} ${styles.buttonSecondary}`}
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    )}
                    <button 
                      type="submit" 
                      className={`${styles.button} ${styles.buttonPrimary}`}
                      disabled={loading}
                    >
                      {loading ? 'Adding...' : `Add ${getSingularTabName(activeTab)}`}
                    </button>
                  </>
                )}
              </div>
            </div>
          </form>
        )}

        {/* Notifications tab - separate form */}
        {activeTab === 'notifications' && (
          <form onSubmit={handleNotificationsSubmit}>
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionHeaderText}>Email Notifications</span>
              </div>
              <div className={styles.formGroup}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '1rem',
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  border: '1px solid rgba(15, 23, 42, 0.1)'
                }}>
                <input
                  type="checkbox"
                  id="sendEmail"
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                  style={{ 
                    width: '18px', 
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <label htmlFor="sendEmail" style={{ 
                    cursor: 'pointer',
                    fontWeight: '500',
                    color: 'var(--text-primary)'
                  }}>
                    Send email notifications to students
                  </label>
                  <p style={{ 
                    margin: '0.5rem 0 0 0',
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)'
                  }}>
                    When enabled, students will receive email notifications when new course content is added.
                    <br />
                    <strong>Note:</strong> This setting applies to all content types and is available to admin users.
                    <br />
                    <strong>Current Status:</strong> 
                    <span style={{ 
                      color: formData.sendEmail ? 'var(--success-color)' : 'var(--error-color)',
                      fontWeight: 'bold',
                      marginLeft: '0.5rem'
                    }}>
                      {formData.sendEmail ? 'ENABLED' : 'DISABLED'}
                    </span>
                  </p>
                </div>
              </div>
              </div>
            </div>

            <div className={styles.inlineFooter}>
              <div className={styles.footerButtons}>
                <button 
                  type="button" 
                  className={`${styles.button} ${styles.buttonSecondary}`}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`${styles.button} ${styles.buttonPrimary}`}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </form>
        )}
      </>
    );
  };

  const getCurrentSubTab = () => {
    switch (activeTab) {
      case 'recordings': return recordingsSubTab;
      case 'announcements': return announcementsSubTab;
      case 'syllabus': return syllabusSubTab;
      case 'slides': return slidesSubTab;
      case 'readings': return readingsSubTab;
      default: return 'new';
    }
  };

  const getSetSubTab = (value) => {
    switch (activeTab) {
      case 'recordings': return () => setRecordingsSubTab(value);
      case 'announcements': return () => setAnnouncementsSubTab(value);
      case 'syllabus': return () => setSyllabusSubTab(value);
      case 'slides': return () => setSlidesSubTab(value);
      case 'readings': return () => setReadingsSubTab(value);
      default: return () => {};
    }
  };

  const currentSubTab = getCurrentSubTab();
  const isInstructor = currentUser?.role === 'instructor' || currentUser?.role === 'admin';
  const recordingExternalLinks = activeTab === 'recordings'
    ? (content.recordings || [])
        .map(item => {
          const url = typeof item.url === 'string' ? item.url.trim() : '';
          const week = item?.week;
          return { week, url };
        })
        .filter(item => Boolean(item.url))
    : [];

  const handleCopyDirectVideoLinks = async () => {
    const seen = new Set();
    const formattedLines = [];

    for (const item of recordingExternalLinks) {
      const normalizedWeek = item.week !== undefined && item.week !== null && `${item.week}`.trim() !== ''
        ? `${item.week}`.trim()
        : 'N/A';
      const line = `Week ${normalizedWeek} - ${item.url}`;
      if (!seen.has(line)) {
        seen.add(line);
        formattedLines.push(line);
      }
    }

    if (formattedLines.length === 0) return;

    await copyTextToClipboard(formattedLines.join('\n'), 'All recording links copied to clipboard.');
  };

  return (
    <>
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>
            {editingContent ? `Edit ${getSingularTabName(activeTab)}` : elective ? 'Manage Elective Content' : 'Manage Course Content'}
          </h1>
          <p className={styles.pageSubtext}>
            Add or update course materials.
          </p>
        </div>
        <div className={styles.headerActions}>
          {onClose && (
            <button className={styles.backButton} onClick={onClose}>
              <FiArrowLeft size={16} />
              Back
            </button>
          )}
        </div>
      </div>

      {/* Two-Column Layout */}
      {effectivePermissions && navItems.length === 0 ? (
        <div className={styles.contentCard}>
          <div className={styles.emptyState}>
            <p>You do not have permission to manage any content sections for this course.</p>
          </div>
        </div>
      ) : (
      <div className={styles.layoutContainer}>
        {/* Sidebar Navigation */}
        <nav className={styles.sidebarNav} aria-label="Content sections">
          <ul className={styles.navList}>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id} className={styles.navItem}>
                  <button
                    className={`${styles.navButton} ${activeTab === item.id ? styles.active : ''}`}
                    onClick={() => handleTabChange(item.id)}
                    aria-current={activeTab === item.id ? 'page' : undefined}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile Dropdown Navigation */}
        <select
          className={styles.mobileNavSelect}
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          aria-label="Select content section"
        >
          {navItems.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          <div className={styles.contentCard}>
            {/* Sub-tabs - Segmented Control */}
            {activeTab !== 'notifications' && (
              <div className={styles.subTabHeaderRow}>
                <div className={styles.segmentedControl}>
                  <button 
                    className={`${styles.segmentedButton} ${currentSubTab === 'new' ? styles.active : ''}`}
                    onClick={getSetSubTab('new')}
                  >
                    New
                  </button>
                  <button 
                    className={`${styles.segmentedButton} ${currentSubTab === 'existing' ? styles.active : ''}`}
                    onClick={getSetSubTab('existing')}
                  >
                    Existing
                  </button>
                </div>
                {activeTab === 'recordings' && currentSubTab === 'existing' && isInstructor && recordingExternalLinks.length > 0 && (
                  <button
                    type="button"
                    className={styles.copyLinksButton}
                    onClick={handleCopyDirectVideoLinks}
                  >
                    <FiCopy size={14} />
                    <span>Copy All Recording Links</span>
                  </button>
                )}
              </div>
            )}

            {/* Form Content */}
            <div className={styles.formContent}>
              {renderForm()}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
    <Notification
      isOpen={notification.isOpen}
      onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
      type={notification.type}
      title={notification.title}
      message={notification.message}
    />
    <ConfirmationModal
      isOpen={confirmModal.isOpen}
      onClose={closeConfirm}
      onConfirm={() => confirmModal.onConfirm && confirmModal.onConfirm()}
      title={confirmModal.title}
      message={confirmModal.message}
      confirmText={confirmModal.confirmText}
      type={confirmModal.type}
      isLoading={confirmModal.isLoading}
    />
    <DeleteRecordingModal
      isOpen={deleteRecordingModal.isOpen}
      loading={deleteRecordingModal.loading}
      deleting={deleteRecordingModal.deleting}
      error={deleteRecordingModal.error}
      recording={deleteRecordingModal.recording}
      serving={deleteRecordingModal.serving}
      ingest={deleteRecordingModal.ingest}
      confirmText={deleteRecordingModal.confirmText}
      onConfirmTextChange={(value) => setDeleteRecordingModal(prev => ({ ...prev, confirmText: value }))}
      onConfirm={performDeleteRecording}
      onClose={closeDeleteRecordingModal}
    />
    <DeleteRecordingModal
      isOpen={overwriteModal.isOpen}
      loading={overwriteModal.loading}
      deleting={overwriteModal.deleting}
      error={overwriteModal.error}
      recording={overwriteModal.recording}
      serving={overwriteModal.serving}
      ingest={overwriteModal.ingest}
      confirmText={overwriteModal.confirmText}
      onConfirmTextChange={(value) => setOverwriteModal(prev => ({ ...prev, confirmText: value }))}
      onConfirm={performOverwriteAndUpload}
      onClose={closeOverwriteModal}
      title="File already exists"
      description={(() => {
        const tail = overwriteModal.mode === 'clear'
          ? 'will be deleted so you can upload a replacement.'
          : 'will be deleted before upload.';
        return overwriteModal.collidingCount > 1
          ? <>A recording already exists for this date. <strong>{overwriteModal.collidingCount} existing recordings</strong> on this date and their associated files {tail}</>
          : <>A recording already exists for this date. The following files associated with this recording {tail}</>;
      })()}
      confirmWord="overwrite"
      confirmLabel="Confirm Overwrite"
      busyLabel="Overwriting…"
    />
    {showMergeModal && course?.id && (
      <MergeRecordingsModal
        courseId={course.id}
        recordings={content.recordings || []}
        onClose={() => setShowMergeModal(false)}
        onMergeSubmitted={() => {
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Merge submitted',
            message: 'The merged recording will appear once processing completes.',
          });
          // Trigger a content refresh
          if (typeof fetchContent === 'function') fetchContent();
        }}
      />
    )}
    <MergeStatusModal
      state={mergeStatusModal}
      onClose={() => setMergeStatusModal(prev => ({ ...prev, isOpen: false }))}
      onRefresh={fetchMergeStatus}
    />
    <UploadCompleteModal
      state={uploadCompleteModal}
      onClose={() => setUploadCompleteModal(prev => ({ ...prev, isOpen: false }))}
      onCheckStatus={() => {
        const { contentId, contentTitle } = uploadCompleteModal;
        setUploadCompleteModal(prev => ({ ...prev, isOpen: false }));
        if (contentId) fetchMergeStatus(contentId, contentTitle);
      }}
    />
    </>
  );
}

// Strip presigned-URL secrets and AWS access-key ids from any text before it
// reaches the UI or logs. Module-level so both the main component and the
// module-level MergeStatusModal can use it.
const sanitizeAwsSensitiveText = (value) => String(value || '')
  .replace(/([?&]X-Amz-Credential=)[^&\s]+/gi, '$1[REDACTED]')
  .replace(/([?&]X-Amz-Signature=)[^&\s]+/gi, '$1[REDACTED]')
  .replace(/([?&]X-Amz-Security-Token=)[^&\s]+/gi, '$1[REDACTED]')
  .replace(/AKIA[0-9A-Z]{16}/g, '[REDACTED_AWS_ACCESS_KEY_ID]')
  .replace(/ASIA[0-9A-Z]{16}/g, '[REDACTED_AWS_ACCESS_KEY_ID]');

const formatMergeTimestamp = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const formatBytes = (value) => {
  const num = Number(value);
  if (!num || Number.isNaN(num)) return '—';
  if (num >= 1024 ** 3) return `${(num / 1024 ** 3).toFixed(2)} GB`;
  if (num >= 1024 ** 2) return `${(num / 1024 ** 2).toFixed(1)} MB`;
  if (num >= 1024) return `${(num / 1024).toFixed(0)} KB`;
  return `${num} B`;
};

// Rough overall % across the merge-upload pipeline so the user has a single
// number that always moves forward. Each phase is allocated ~33%.
const computeOverallPercent = (data) => {
  if (!data) return null;
  const status = data.status || data.dbStatus;
  if (status === 'ready' || status === 'COMPLETE' || data.dbStage === 'ready') return 100;
  if (status === 'failed' || data.dbStage === 'failed') return null;
  if (data.kind !== 'merge-upload') {
    return typeof data.percentComplete === 'number' ? data.percentComplete : null;
  }
  const expected = data.expectedSourceCount || 0;
  if (!expected) return null;
  const uploadedFrac = Math.min(1, (data.uploadedCount || 0) / expected);
  const readyFrac = Math.min(1, (data.readyCount || 0) / expected);
  const mergeFrac = typeof data.percentComplete === 'number' ? data.percentComplete / 100 : 0;
  if (data.dbStage === 'uploading') return Math.round(uploadedFrac * 33);
  if (data.dbStage === 'converting') return Math.round(33 + readyFrac * 33);
  if (data.dbStage === 'merging') return Math.round(66 + mergeFrac * 34);
  return Math.round(uploadedFrac * 33);
};

const ProgressBar = ({ percent, color = '#b88652' }) => {
  const safe = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <div style={{ width: '100%', height: 8, background: 'rgba(15,23,42,0.06)', borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ width: `${safe}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.4s ease' }} />
    </div>
  );
};

// Translates the raw DB/AWS state into a one-line plain-English summary
// of what is happening right now in the pipeline.
const describePipelineState = (data) => {
  if (!data) return null;
  // When the live AWS lookup failed, defer to the message + red AWS-error box
  // rather than showing an optimistic pipeline summary that contradicts it.
  if (data.awsError) return null;
  const kind = data.kind;
  const stage = data.dbStage;
  const status = data.status || data.dbStatus;
  const uploaded = data.uploadedCount ?? 0;
  const expected = data.expectedSourceCount ?? null;
  const ready = data.readyCount ?? 0;

  if (status === 'failed' || status === 'ERROR' || stage === 'failed') {
    return { tone: 'error', text: 'The pipeline failed. See the error message below for details.' };
  }
  if (status === 'ready' || status === 'COMPLETE' || stage === 'ready') {
    return { tone: 'success', text: 'Processing complete.' };
  }

  if (kind === 'merge-upload') {
    if (stage === 'uploading') {
      return {
        tone: 'info',
        text: `Uploading source files to S3 — ${uploaded} of ${expected ?? '?'} received. The merge will start once every source is uploaded and individually converted.`,
      };
    }
    if (stage === 'converting') {
      return {
        tone: 'info',
        text: `All sources uploaded. MediaConvert is converting each one to MP4 — ${ready} of ${expected ?? uploaded} done. The merge job is submitted automatically once every source is converted.`,
      };
    }
    if (stage === 'merging') {
      return {
        tone: 'info',
        text: 'All sources converted. MediaConvert is now stitching them together into the final merged MP4.',
      };
    }
  }

  if (kind === 'merge') {
    if (status === 'processing' || status === 'PROGRESSING' || status === 'SUBMITTED') {
      return { tone: 'info', text: 'A manual merge job is running on MediaConvert. The output will appear once it finishes.' };
    }
  }

  if (status === 'PROGRESSING') {
    if (kind === 'merge-upload' || kind === 'merge') {
      return { tone: 'info', text: `MediaConvert is processing the merge (${data.percentComplete ?? 0}% complete).` };
    }
    return {
      tone: 'info',
      text: 'File Uploaded successfully. Awaiting conversion to streaming format.',
    };
  }
  if (status === 'SUBMITTED') {
    if (kind === 'merge-upload' || kind === 'merge') {
      return { tone: 'info', text: 'Merge job submitted to MediaConvert and waiting to start.' };
    }
    return {
      tone: 'info',
      text: 'File uploaded successfully to the ingest bucket. The conversion job has been submitted to MediaConvert and will start shortly.',
    };
  }
  // Single-file recording whose live AWS job hasn't been resolved yet (the S3
  // event → MediaConvert handoff takes a moment). The DB row is still 'processing'.
  if (status === 'processing' && kind !== 'merge-upload' && kind !== 'merge') {
    return {
      tone: 'info',
      text: 'File uploaded successfully to the ingest bucket. Conversion is queued and will begin shortly.',
    };
  }
  return null;
};

const formatClock = (value) => {
  if (!value) return null;
  try { return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return null; }
};

// Discrete upload → convert → serve stages for a single-file recording, so the
// upload-to-ingest step is reported explicitly (mirrors the multi-file
// PipelineStages). The file is in the ingest bucket by the time a row/job
// exists, so the upload stage is always shown complete.
function SingleFileStages({ data }) {
  const status = data.status || data.dbStatus;
  const isReady = status === 'ready' || status === 'COMPLETE' || data.dbStage === 'ready';
  const isFailed = status === 'failed' || status === 'ERROR' || data.dbStage === 'failed';
  const pct = typeof data.percentComplete === 'number' ? data.percentComplete : null;

  const stages = [
    { label: 'Upload Status', state: 'done', detail: 'Done' },
    {
      label: 'Conversion Status',
      state: isReady ? 'done' : isFailed ? 'failed' : 'active',
      detail: isReady ? 'Done' : isFailed ? 'Failed' : (pct != null ? `In progress · ${pct}%` : 'Queued'),
    },
    {
      label: 'Final Status',
      state: isReady ? 'done' : isFailed ? 'failed' : 'pending',
      detail: isReady ? 'Done' : isFailed ? '—' : 'Pending',
    },
  ];

  const palette = {
    done: { icon: '#16a34a', detail: '#15803d' },
    active: { icon: '#b88652', detail: '#b88652' },
    failed: { icon: '#dc2626', detail: '#dc2626' },
    pending: { icon: '#9ca3af', detail: '#9ca3af' },
  };
  const iconFor = (state) => {
    if (state === 'done') return <FiCheckCircle size={18} color={palette.done.icon} />;
    if (state === 'failed') return <FiAlertCircle size={18} color={palette.failed.icon} />;
    if (state === 'active') return <FiActivity size={18} color={palette.active.icon} />;
    return <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid #d8dce1' }} />;
  };

  return (
    <div style={{ marginTop: 4 }}>
      {stages.map((s, i) => {
        const last = i === stages.length - 1;
        const c = palette[s.state] || palette.pending;
        return (
          <div key={i} style={{ display: 'flex', gap: 12 }}>
            {/* Rail: status icon + connector line to the next step */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20 }}>
                {iconFor(s.state)}
              </div>
              {!last && <div style={{ width: 2, flex: 1, minHeight: 20, background: '#eef0f2', borderRadius: 2, margin: '2px 0' }} />}
            </div>
            <div style={{ flex: 1, paddingBottom: last ? 0 : 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>{s.label}</div>
              <div style={{ marginTop: 2, fontSize: 12, fontWeight: 500, color: c.detail }}>{s.detail}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Stage-by-stage breakdown of the merge-upload pipeline. Mirrors the
// status-dashboard layout an admin would use to diagnose where things are.
function PipelineStages({ data, mergeExpanded, setMergeExpanded, uploadExpanded, setUploadExpanded, conversionExpanded, setConversionExpanded }) {
  const sources = Array.isArray(data.sources) ? data.sources : [];
  const expected = data.expectedSourceCount ?? sources.length;
  const uploaded = data.uploadedCount ?? 0;
  const ready = data.readyCount ?? sources.filter(s => s.status === 'ready').length;
  const allUploaded = expected > 0 && uploaded >= expected;
  const allConverted = expected > 0 && ready >= expected;
  const isReady = (data.status || data.dbStatus) === 'ready' || data.dbStage === 'ready';
  const conversionsInFlight = sources.filter(s => s.status === 'processing');
  const conversionsFailed = sources.filter(s => s.status === 'failed');

  // After the merge completes the per-source rows are deleted, but the
  // placeholder retains source_keys — fall back to it so Upload/Conversion can
  // still list the files on a finished recording.
  const sourceKeys = Array.isArray(data.sourceKeys) ? data.sourceKeys : [];
  const sourceFileNames = Array.isArray(data.sourceFileNames) ? data.sourceFileNames : [];
  const displaySources = sources.length > 0
    ? sources
    : sourceKeys.map((k, i) => ({
        id: `key-${i}`,
        order: i + 1,
        key: k,
        fileName: sourceFileNames[i] || k.split('/').pop(),
        status: isReady ? 'ready' : null,
      }));

  let conversionSummary = '—';
  if (isReady) conversionSummary = '✓ COMPLETE';
  else if (allConverted) conversionSummary = '✓ COMPLETE';
  else if (conversionsFailed.length > 0) conversionSummary = `${conversionsFailed.length} FAILED`;
  else if (conversionsInFlight.length > 0) conversionSummary = `${conversionsInFlight.length} PROGRESSING · ${ready}/${expected} done`;
  else if (allUploaded) conversionSummary = 'Waiting to start';
  else conversionSummary = 'Pending — sources still uploading';

  const mergeHasStarted = !!data.jobId;
  let mergeStatusBadge;
  if (isReady) {
    mergeStatusBadge = '✓ COMPLETE';
  } else if (mergeHasStarted) {
    const pct = typeof data.percentComplete === 'number' ? `${data.percentComplete}%` : '—';
    const phase = data.phase ? ` · ${data.phase}` : '';
    mergeStatusBadge = `${data.status || 'PROGRESSING'} · ${pct}${phase}`;
  } else if (allConverted) {
    mergeStatusBadge = 'Submitting…';
  } else {
    mergeStatusBadge = 'PENDING';
  }

  const finalState = isReady ? 'COMPLETED' : 'PENDING';
  const finalDetail = isReady
    ? `COMPLETED (${data.finalFileName || '—'})`
    : `PENDING${data.finalFileName ? ` (target: ${data.finalFileName})` : ''}`;

  const rowStyle = { display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(15,23,42,0.06)' };
  const stageLabel = { fontSize: 12, fontWeight: 600, color: '#374151' };
  const stageDetail = { fontSize: 12, color: '#1f2937', lineHeight: 1.45 };

  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(15,23,42,0.08)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Pipeline stages</div>

      <div style={rowStyle}>
        <span style={stageLabel}>
          <button
            type="button"
            onClick={() => displaySources.length > 0 && setUploadExpanded(!uploadExpanded)}
            style={{
              background: 'none', border: 'none', cursor: displaySources.length > 0 ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontWeight: 600, fontSize: 12, color: '#374151'
            }}
            disabled={displaySources.length === 0}
          >
            Upload Status
            {displaySources.length > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', transition: 'transform 0.2s' }}>
                {uploadExpanded ? '▼' : '▶'}
              </span>
            )}
          </button>
        </span>
        <span style={stageDetail}>
          {isReady
            ? '✓ COMPLETE'
            : displaySources.length === 0
              ? 'No source rows yet — waiting for client upload to register.'
              : allUploaded
                ? '✓ COMPLETE'
                : `${uploaded} of ${expected ?? '?'} uploaded`
          }
        </span>
      </div>

      {uploadExpanded && displaySources.length > 0 && (
        <div style={{ ...rowStyle, borderBottom: 'none', paddingLeft: 20, flexDirection: 'column', gap: 8 }}>
          <ul style={{ margin: 0, padding: 0, fontSize: 11, color: '#374151' }}>
            {displaySources.map(src => (
              <li key={src.id} style={{ marginBottom: 6, listStyle: 'none' }}>
                <span style={{ fontFamily: 'ui-monospace, monospace' }}>{src.fileName || src.key?.split('/').pop() || '—'}</span>
                {src.fileSize ? <span style={{ color: '#6b7280' }}> ({formatBytes(src.fileSize)})</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={rowStyle}>
        <span style={stageLabel}>
          <button
            type="button"
            onClick={() => displaySources.length > 0 && setConversionExpanded(!conversionExpanded)}
            style={{
              background: 'none', border: 'none', cursor: displaySources.length > 0 ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontWeight: 600, fontSize: 12, color: '#374151'
            }}
            disabled={displaySources.length === 0}
          >
            Conversion Status
            {displaySources.length > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', transition: 'transform 0.2s' }}>
                {conversionExpanded ? '▼' : '▶'}
              </span>
            )}
          </button>
        </span>
        <span style={stageDetail}>{conversionSummary}</span>
      </div>

      {conversionExpanded && displaySources.map(src => {
        const color = src.status === 'ready' ? '#16a34a' : src.status === 'failed' ? '#dc2626' : '#b88652';
        const started = formatClock(src.awsStartedAt) || formatClock(src.awsSubmittedAt);
        let detail;
        if (src.status === 'ready') {
          detail = '✓ COMPLETE';
        } else if (src.status === 'failed') {
          detail = `FAILED${src.errorMessage ? ` — ${src.errorMessage}` : ''}`;
        } else if (typeof src.awsPercentComplete === 'number') {
          detail = `${src.awsPercentComplete}% — ${src.awsPhase || 'TRANSCODING'}`;
        } else if (src.awsStatus) {
          detail = src.awsStatus;
        } else {
          detail = 'Queued';
        }
        return (
          <div key={src.id} style={rowStyle}>
            <span style={{ ...stageLabel, paddingLeft: 14, color: '#6b7280', fontWeight: 500 }}>
              └ {src.fileName || `source-${src.order}`}{started ? ` (started ${started})` : ''}
            </span>
            <span style={{ ...stageDetail, color, fontWeight: 600 }}>{detail}</span>
            {src.status === 'processing' && typeof src.awsPercentComplete === 'number' && (
              <span style={{ gridColumn: '2 / 3', marginTop: 2 }}><ProgressBar percent={src.awsPercentComplete} /></span>
            )}
          </div>
        );
      })}

      <div style={rowStyle}>
        <span style={stageLabel}>
          <button
            type="button"
            onClick={() => mergeHasStarted && setMergeExpanded(!mergeExpanded)}
            style={{
              background: 'none', border: 'none', cursor: mergeHasStarted ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontWeight: 600, fontSize: 12, color: '#374151'
            }}
            disabled={!mergeHasStarted}
          >
            Merge Status
            {mergeHasStarted && (
              <span style={{ display: 'inline-flex', alignItems: 'center', transition: 'transform 0.2s' }}>
                {mergeExpanded ? '▼' : '▶'}
              </span>
            )}
          </button>
        </span>
        <span style={stageDetail}>{mergeStatusBadge}</span>
      </div>

      {mergeExpanded && mergeHasStarted && (
        <>
          {data.jobId && (
            <div style={{ ...rowStyle, paddingLeft: 20 }}>
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Job ID</span>
              <span style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#1f2937', wordBreak: 'break-all' }}>{data.jobId}</span>
            </div>
          )}
          <div style={{ ...rowStyle, paddingLeft: 20 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Submitted</span>
            <span style={{ fontSize: 11, color: '#1f2937' }}>{formatMergeTimestamp(data.submittedAt)}</span>
          </div>
          <div style={{ ...rowStyle, paddingLeft: 20 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Started</span>
            <span style={{ fontSize: 11, color: '#1f2937' }}>{formatMergeTimestamp(data.startedAt)}</span>
          </div>
          <div style={{ ...rowStyle, paddingLeft: 20 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Finished</span>
            <span style={{ fontSize: 11, color: '#1f2937' }}>{formatMergeTimestamp(data.finishedAt)}</span>
          </div>
        </>
      )}

      <div style={{ ...rowStyle, borderBottom: 'none' }}>
        <span style={stageLabel}>Final Status</span>
        <span style={finalState === 'COMPLETED' ? { ...stageDetail, color: '#16a34a', fontWeight: 600 } : stageDetail}>
          {finalDetail}
        </span>
      </div>
    </div>
  );
}

// Post-upload confirmation with "Check Status" / "Done" buttons. Replaces the
// old toast so users can jump straight into the live pipeline status.
function UploadCompleteModal({ state, onClose, onCheckStatus }) {
  const [closeHover, setCloseHover] = React.useState(false);
  const [secondaryHover, setSecondaryHover] = React.useState(false);
  const [primaryHover, setPrimaryHover] = React.useState(false);

  if (!state?.isOpen) return null;

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2400, padding: '1rem',
  };
  const cardStyle = {
    background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480,
    boxShadow: '0 20px 50px rgba(15,23,42,0.12)', fontFamily: 'Inter, sans-serif',
    border: '1px solid rgba(15,23,42,0.06)', overflow: 'hidden',
  };
  const contentStyle = {
    display: 'flex', flexDirection: 'column', gap: 24, padding: 24,
  };
  const headerStyle = {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
  };
  const titleGroupStyle = {
    display: 'flex', alignItems: 'center', gap: 12, flex: 1,
  };
  const iconWrapStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 40, height: 40, borderRadius: 10, background: '#dcfce7', color: '#16a34a',
    flexShrink: 0,
  };
  const titleStyle = {
    fontWeight: 600, fontSize: 19, color: '#0f172a', lineHeight: 1.2, margin: 0,
  };
  const closeButtonStyle = {
    background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
    padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 6, transition: 'all 150ms ease', flexShrink: 0,
  };
  const closeButtonHoverStyle = {
    ...closeButtonStyle,
    background: 'rgba(15,23,42,0.05)', color: '#6b7280',
  };
  const bodyStyle = {
    display: 'flex', flexDirection: 'column', gap: 12, color: '#4b5563',
    fontSize: 14, lineHeight: 1.5, marginLeft: 52,
  };
  const paragraphPrimaryStyle = { margin: 0, color: '#4b5563', fontSize: 13.5, lineHeight: 1.5 };
  const paragraphSecondaryStyle = {
    margin: 0, color: '#6b7280', fontSize: 13.5, lineHeight: 1.5,
  };
  const footerStyle = {
    display: 'flex', justifyContent: 'flex-end', gap: 12,
  };
  const secondaryButtonStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '10px 18px', borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)',
    background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 500,
    color: '#4b5563', transition: 'all 150ms ease',
  };
  const secondaryButtonHoverStyle = {
    ...secondaryButtonStyle,
    background: 'rgba(15,23,42,0.04)', borderColor: 'rgba(15,23,42,0.15)',
  };
  const primaryButtonStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '10px 20px', borderRadius: 8, border: 'none',
    background: '#b88652', cursor: 'pointer', fontSize: 14, fontWeight: 600,
    color: '#fff', transition: 'all 150ms ease',
  };
  const primaryButtonHoverStyle = {
    ...primaryButtonStyle,
    background: '#a3763e',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={contentStyle}>
          <div style={headerStyle}>
            <div style={titleGroupStyle}>
              <span style={iconWrapStyle}><FiCheckCircle size={22} /></span>
              <h2 style={titleStyle}>Upload complete</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              onMouseEnter={() => setCloseHover(true)}
              onMouseLeave={() => setCloseHover(false)}
              style={closeHover ? closeButtonHoverStyle : closeButtonStyle}
            >
              <FiX size={18} />
            </button>
          </div>
          <div style={bodyStyle}>
            <p style={paragraphPrimaryStyle}>
              Your files have been uploaded successfully. We&rsquo;re now preparing your recording in the background.
            </p>
            <p style={paragraphSecondaryStyle}>
              Please allow 10 to 60 minutes for this process to complete. You can check the latest progress at any time using <span style={{ fontWeight: 500, color: '#4b5563' }}>Check Status</span>.
            </p>
          </div>
          <div style={footerStyle}>
            <button
              type="button"
              onClick={onClose}
              onMouseEnter={() => setSecondaryHover(true)}
              onMouseLeave={() => setSecondaryHover(false)}
              style={secondaryHover ? secondaryButtonHoverStyle : secondaryButtonStyle}
            >
              Done
            </button>
            <button
              type="button"
              onClick={onCheckStatus}
              onMouseEnter={() => setPrimaryHover(true)}
              onMouseLeave={() => setPrimaryHover(false)}
              style={primaryHover ? primaryButtonHoverStyle : primaryButtonStyle}
            >
              <FiActivity size={16} /> Check Status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MergeStatusModal({ state, onClose, onRefresh }) {
  const [mergeExpanded, setMergeExpanded] = React.useState(false);
  const [uploadExpanded, setUploadExpanded] = React.useState(false);
  const [conversionExpanded, setConversionExpanded] = React.useState(false);

  if (!state?.isOpen) return null;
  const { loading, contentTitle, contentId, data, error, lastFetchedAt } = state;
  const handleRefresh = () => {
    if (!contentId || loading) return;
    if (typeof onRefresh === 'function') onRefresh(contentId, contentTitle);
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2400, padding: '1rem',
  };
  const cardStyle = {
    background: '#fff', borderRadius: 8, width: '100%', maxWidth: 520,
    boxShadow: '0 24px 48px rgba(15,23,42,0.18)', fontFamily: 'Inter, sans-serif',
    // Constrain to the viewport (overlay has 1rem padding top+bottom) and lay
    // out as a column so the body can scroll while header/footer stay fixed.
    maxHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column',
  };
  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', borderBottom: '1px solid rgba(15,23,42,0.08)',
    flexShrink: 0,
  };
  const bodyStyle = {
    padding: '18px', color: '#1f2937', fontSize: 13, lineHeight: 1.5,
    flex: 1, overflowY: 'auto', minHeight: 0,
  };
  const rowStyle = { display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, padding: '6px 0' };
  const labelStyle = { color: '#6b7280', fontWeight: 500 };
  const valueStyle = { color: '#1f2937', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' };
  const footerStyle = {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    padding: '12px 18px', borderTop: '1px solid rgba(15,23,42,0.08)',
    flexShrink: 0,
  };
  const buttonStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(15,23,42,0.12)',
    background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#1f2937',
  };
  const primaryButtonStyle = { ...buttonStyle, background: '#b88652', borderColor: '#b88652', color: '#fff' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 15 }}>
            <FiActivity size={16} color="#b88652" />
            Course Recording Status
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={18} />
          </button>
        </div>
        <div style={bodyStyle}>
          {contentTitle && (
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 16 }}>{contentTitle}</div>
          )}
          {loading && <div style={{ fontSize: 13, color: '#6b7280' }}>Fetching latest status from AWS…</div>}
          {!loading && error && (
            <div style={{ color: '#dc2626' }}>
              <FiAlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {error}
            </div>
          )}
          {!loading && !error && data && (
            <>
              {(() => {
                const summary = describePipelineState(data);
                if (!summary) return null;
                const overall = computeOverallPercent(data);
                const hasBar = typeof overall === 'number';
                const accent = summary.tone === 'error' ? '#dc2626' : summary.tone === 'success' ? '#16a34a' : '#b88652';
                return (
                  <div style={{ background: '#f9fafb', border: '1px solid #f0f1f3', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: hasBar ? 12 : 0 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0, marginTop: 5 }} />
                      <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.45, fontWeight: 500 }}>{summary.text}</span>
                    </div>
                    {hasBar && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1 }}><ProgressBar percent={overall} color={accent} /></div>
                        <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: 13, color: '#111827', minWidth: 38, textAlign: 'right' }}>{overall}%</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              {data.message && !describePipelineState(data) && (
                <div style={{ color: '#6b7280', marginBottom: 8 }}>{data.message}</div>
              )}
              {data.awsError && (
                <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 12, lineHeight: 1.45 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiAlertCircle size={14} /> AWS error
                  </div>
                  <div style={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-word' }}>
                    {sanitizeAwsSensitiveText(data.awsError)}
                  </div>
                </div>
              )}
              {/* Merge-upload only: the final merge job's own percent + phase add
                  detail beyond the composite Overall Progress bar. For single-file
                  and manual merges the Overall Progress bar already shows this exact
                  percent, so these rows would just repeat it. */}
              {data.kind === 'merge-upload' && typeof data.percentComplete === 'number' && (
                <div style={rowStyle}>
                  <span style={labelStyle}>Merge progress</span>
                  <span style={{ ...valueStyle, fontFamily: 'inherit' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1 }}><ProgressBar percent={data.percentComplete} /></div>
                      <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>{data.percentComplete}%</span>
                    </div>
                  </span>
                </div>
              )}
              {data.kind === 'merge-upload' && data.phase && (
                <div style={rowStyle}>
                  <span style={labelStyle}>Phase</span>
                  <span style={valueStyle}>{data.phase}</span>
                </div>
              )}
              {data.errorCode != null && (
                <div style={rowStyle}>
                  <span style={labelStyle}>Error code</span>
                  <span style={{ ...valueStyle, color: '#dc2626' }}>{data.errorCode}</span>
                </div>
              )}
              {data.errorMessage && (
                <div style={rowStyle}>
                  <span style={labelStyle}>Error</span>
                  <span style={{ ...valueStyle, color: '#dc2626' }}>{data.errorMessage}</span>
                </div>
              )}
              {data.kind === 'merge-upload' && (
                <PipelineStages data={data} mergeExpanded={mergeExpanded} setMergeExpanded={setMergeExpanded} uploadExpanded={uploadExpanded} setUploadExpanded={setUploadExpanded} conversionExpanded={conversionExpanded} setConversionExpanded={setConversionExpanded} />
              )}
              {data.kind !== 'merge-upload' && data.kind !== 'merge' && !data.awsError && (
                <SingleFileStages data={data} />
              )}
            </>
          )}
        </div>
        <div style={footerStyle}>
          <span style={{ flex: 1, fontSize: 11, color: '#6b7280', alignSelf: 'center' }}>
            {loading ? 'Fetching…' : lastFetchedAt ? `Last refreshed: ${new Date(lastFetchedAt).toLocaleTimeString()}` : ''}
          </span>
          <button type="button" style={buttonStyle} onClick={handleRefresh} disabled={loading || !contentId}>
            <FiRefreshCw size={13} />
            Refresh
          </button>
          <button type="button" style={primaryButtonStyle} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default AddContentModal;
